import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Link,
  Autocomplete,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Comment as CommentIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  DoneAll as DoneAllIcon,
  FiberManualRecord as UnreadIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import CoachPresenceControl from './CoachPresenceControl';
import { PresenceWindow, validateWindow } from '../utils/coachPresence';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { discardChangesConfirm } from '../utils/formDirty';
import { useFeedback, useToastObstacle } from '../contexts/FeedbackContext';
import { useResponsive } from '../hooks/useResponsive';
import { MOBILE_SCROLLER_PADDING_BOTTOM, MOBILE_SCROLLER_PADDING_X } from './dashboardLayout';
import type { Theme } from '@mui/material/styles';
import {
  ATTENDANCE_TABLE_ELEVATION,
  attendanceCardBg,
  attendanceMarkBg,
  attendanceToneText,
  collectionRowColors,
  unsavedCounterColor,
  SAVE_BAR_ELEVATION,
} from '../theme/readableColors';
import { API_BASE_URL } from '../utils/config';
import { NETWORK_ERROR_MESSAGE } from '../utils/apiErrors';
// [3. javító kör, F-1, 2026-10-03] lejárat-tudatos burkolók (`utils/saveErrors.ts`) — az axios- és a
// `fetch`-ág is ezeken át ad szöveget (őr: `components/__tests__/unsavedCoverage.test.ts`).
import {
  requestErrorMessage,
  requestResponseErrorMessage,
  saveResponseErrorMessage,
} from '../utils/saveErrors';
// [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03] A `fetch`-ágak 401-ét semmilyen axios-interceptor
// nem látja (leltár 2(a)): minden hitelesített hívás után a közös jelzőnek adjuk át (`utils/session.ts`).
import { isSessionExpiredLocked } from '../utils/session';
// [Web 7 / T1, 2026-10-05] a hitelesített `fetch`-ek a közös `authFetch` burkolón mennek: token, csendes
// megújítás, 401 utáni egyszeri ismétlés és a lejárat-jelzés (`reportFetchUnauthorized`) EGY helyen.
import { authFetch } from '../utils/authFetch';
import { formatDateToLocal, formatServerDateTime, parseDateLocal } from '../utils/dateUtils';
import { isForintInputAccepted, parseForintInput, forintFieldValue, isUnfinishedForintDraft, unfinishedForintMessage, MAX_ATTENDANCE_PAYMENT, formatForint } from '../utils/money';
import trainingBalanceService from '../services/trainingBalanceService';
import { userNoteService } from '../services/userNoteService';
import { calendarService } from '../services/calendarService';
import { userService } from '../services/userService';
// A szerepkör-döntések EGY helyen, a GÉPI névből [B-W4, 2026-09-16] — lásd `utils/roles.ts`.
import { isCoachRole } from '../utils/roles';
import { normalizeForSearch } from '../utils/nameSearch';
import { userOptionLabel } from '../utils/userLabel';

interface User {
  userId: string;
  name: string;
  /**
   * [C10, 2026-10-30] `null` is lehet: a szerver edzőnek MÁS tag e-mailjét/telefonját nem adja ki
   * (`backend/PinyoApi/Services/UserListSanitizer.cs`). Kiírni CSAK `userOptionLabel`-lel szabad.
   */
  email: string | null;
  phone?: string | null;
  isActive?: boolean;
  userRoleName?: string; // szerep MEGJELENÍTETT neve (pl. "Edző", "Játékos") — csak kiíráshoz
  /**
   * A szerepkör GÉPI neve (`Coach` / `Player` / `Administrator`) — EBBŐL dől el, edző-e a sor
   * [B-W4, 2026-09-16]. Opcionális, mert a régebbi válasz-alakok (és a régi tesztfixture-ök)
   * nem hordozzák; a `utils/roles.ts` ilyenkor a megjelenített névre esik vissza.
   */
  userRoleSystemName?: string;
  privateLessonFee?: number; // magánóra egyéni díja (Ft) — csak IndividualTraining eseményen, csak a saját edzőnek/adminnak
}

interface UserRole {
  userRoleId: string;
  name: string;
  displayName: string;
  isActive: boolean;
}

interface AttendanceRecord {
  attendanceId?: string;
  userId: string;
  userName: string;
  // [C10, 2026-10-30] a szerver edzőnek MÁS sorában `null`-t ad (`AttendanceController` → `UserListSanitizer`);
  // a jelenléti ív nem írja ki.
  userEmail?: string | null;
  isPresent: boolean;
  hasPaidOnSite: boolean;
  isMakeup: boolean;
  paymentAmount?: number;
  recordedBy?: string;
  recordedByName?: string;
  recordedDate?: Date;
  unreadNotesCount?: number;
  privateLessonFee?: number; // magánóra egyéni díja (Ft) — a helyszíni beszedéshez, ha a backend visszaadta
  // Edző részleges jelenléte ('HH:mm:ss'); mindkettő üres = végig jelen volt. Csak edző-soron értelmezett.
  presentFrom?: string | null;
  presentUntil?: string | null;
}

interface Event {
  eventId: string;
  eventName: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  // A GET /api/events (lista és egy-elem is) LAPOS mezőket ad vissza — nincs beágyazott
  // eventType/venue objektum (lásd backend/PinyoApi/DTOs/EventDto.cs EventTypeName/
  // EventTypeDisplayName/VenueName). A korábbi beágyazott alak némán soha nem illeszkedett.
  eventTypeName?: string;
  eventTypeDisplayName?: string;
  venueName?: string;
  maxParticipants?: number;
  isPaidOut?: boolean;
  /**
   * Az esemény létrehozója (`EventDto.CreatedBy`). A szerver a jelenlét-rögzítést ehhez ÉS az
   * edző-hozzárendeléshez méri (`AttendanceController.IsAdminOrAssignedCoachAsync`), ezért a
   * felületnek is ezt kell vizsgálnia — különben idegen eseményen aktív pipákat mutat, amit
   * aztán a szerver 403-cal utasít el.
   */
  createdBy?: string;
}

interface EventComment {
  commentId: string;
  comment: string;
  coachName: string;
  createdDate: string;
}

interface EventAttendanceProps {
  onNavigateToUserProfile?: (userId: string) => void;
  refreshTrigger?: number;
  /**
   * Egy KONKRÉT eseményre nyitja a jelenléti ívet (az esemény ablakából érkező „Jelenlét”
   * gomb), hogy ne kelljen dátumot és eseményt újra kikeresni. A dátum is kell hozzá:
   * a lista napra szól, előbb arra a napra kell állni.
   */
  initialEventId?: string;
  initialEventDate?: string;
}

/**
 * Ki kezelheti (hozzáad/eltávolít) egy esemény résztvevőit: az admin mindig; a coach
 * csak akkor, ha résztvevője/hozzárendeltje az eseménynek. Kiemelve, hogy a jogosultsági
 * szabály render nélkül tesztelhető legyen (a korábbi bug: az admin ki volt zárva).
 */
export function computeCanManageParticipants(
  isAdmin: boolean,
  isCoach: boolean,
  isCoachParticipant: boolean
): boolean {
  return isAdmin || (isCoach && isCoachParticipant);
}

/**
 * MIÉRT nem szerkeszthető az ív — vagy `null`, ha szerkeszthető.
 *
 * MI VOLT A BAJ [BL-4, 2026-09-15]: a `fetchEventParticipants` `catch`-e csak `console.error`-t
 * írt és `setUsers([])`-t hívott, a jogosultság pedig EBBŐL az üres névsorból vezette le, hogy
 * hozzárendelt edző-e a belépett felhasználó (`users.some(...)`). Egy ÁTMENETI hálózati hiba így
 * az EGÉSZ ívet zárolta — minden pipa, minden összeg-mező és a Mentés letiltva —, HIBAÜZENET
 * NÉLKÜL. A felhasználó azt látta, hogy nincs joga, holott csak a lekérés bukott el.
 *
 * A DÖNTÉS: a „NEM TUDJUK" nem egyenlő a „NEM JOGOSULT"-tal. A jogosultság csak BETÖLTÖTT
 * névsorból dönthető el; betöltetlen/elbukott állapotban a képernyő NEM néma „nem"-et mond, hanem
 * `'participantsUnknown'`-t ad vissza, amit a felület KIÍR, újrapróbálkozási lehetőséggel.
 *
 * ⚠️ A JOG NEM NYÍLIK MEG: ismeretlen állapotban is zárt marad a szerkesztés (a szerver az igazi
 * őr — `AttendanceController.IsAdminOrAssignedCoachAsync`, a bulk mentés 403-at ad idegen
 * eseményre). A javítás CSAK a néma állapotot szünteti meg.
 */
export type AttendanceLockReason =
  /** Kifizetett esemény — senki nem módosíthatja (a szerver is 400-at ad). */
  | 'paidOut'
  /** Nem jogosult (idegen esemény edzője, vagy játékos). */
  | 'notAllowed'
  /** A résztvevő-névsor nincs betöltve, ezért a jogosultság nem megállapítható. */
  | 'participantsUnknown'
  | null;

export function attendanceLockReason(opts: {
  isAdmin: boolean;
  isCoach: boolean;
  isPaidOut: boolean;
  isEventCreator: boolean;
  isAssignedCoach: boolean;
  /** Sikeresen betöltött-e a résztvevő-névsor (ebből dől el az `isAssignedCoach`). */
  participantsLoaded: boolean;
}): AttendanceLockReason {
  if (opts.isPaidOut) return 'paidOut';
  if (opts.isAdmin) return null;
  if (!opts.isCoach) return 'notAllowed';
  // A LÉTREHOZÓ-ág a névsortól FÜGGETLEN (az `Event.createdBy`-ból jön), ezért betöltetlen
  // névsor mellett is érvényes döntés — itt nincs „nem tudjuk".
  if (opts.isEventCreator) return null;
  if (opts.isAssignedCoach) return null;
  if (!opts.participantsLoaded) return 'participantsUnknown';
  return 'notAllowed';
}

/**
 * A jelenléti sor MENTÉSRE VÁRÓ mezői — a baseline-összevetés EGYETLEN forrása.
 *
 * MIÉRT KIEMELVE [mentetlen-változás védelem, 2026-09-13]: ugyanezt a hat mezőt három helyen kell
 * összevetni a betöltéskori alapvonallal (az összefésülő betöltőben, a mentés diffjében és a
 * mentetlen-változás bejelentésében). Amíg a lista külön-külön szerepelt, egy új mező (pl. az edző
 * részleges jelenléte) csendben kimaradhatott az egyikből: a felület zöldet mutat, a változás mégsem
 * mentődik, vagy a fül-váltás kérdés nélkül eldobja. Egy lista, egy függvény, render nélkül tesztelhető.
 */
export const ATTENDANCE_DIRTY_FIELDS = [
  'isPresent', 'hasPaidOnSite', 'isMakeup', 'paymentAmount', 'presentFrom', 'presentUntil',
] as const;

/** A jelenléti sor mentés szempontjából érdekes értékei (a baseline egy bejegyzése). */
export interface AttendanceSnapshot {
  isPresent: boolean;
  hasPaidOnSite: boolean;
  isMakeup: boolean;
  paymentAmount?: number;
  presentFrom?: string | null;
  presentUntil?: string | null;
}

/**
 * Eltér-e a sor a betöltéskori alapvonalától. `undefined` és `null` EGYENÉRTÉKŰ (`?? null`): a szerver
 * a hiányzó összeget/időablakot `null`-ként adja vissza, a felület `undefined`-ként tartja — e kettő
 * különbsége nem felhasználói módosítás, és korábban hamis „piszkos" sort eredményezett.
 * Alapvonal (vagy sor) nélkül NEM piszkos: a betöltés közbeni állapot nem mentetlen változás.
 */
export function isAttendanceRowDirty(
  base: AttendanceSnapshot | undefined,
  record: AttendanceSnapshot | undefined
): boolean {
  if (!base || !record) return false;
  return ATTENDANCE_DIRTY_FIELDS.some(
    (f) => ((base as any)[f] ?? null) !== ((record as any)[f] ?? null)
  );
}

/** Mely sorok (userId) tartalmaznak mentetlen módosítást a betöltéskori alapvonalhoz képest. */
export function computeDirtyUserIds(
  baseline: Map<string, AttendanceSnapshot>,
  records: Array<{ userId: string } & AttendanceSnapshot>
): string[] {
  return records.filter((r) => isAttendanceRowDirty(baseline.get(r.userId), r)).map((r) => r.userId);
}

/** Van-e BÁRMELYIK soron mentetlen módosítás — ezt jelenti be a képernyő a kilépési pontoknak. */
export function hasUnsavedAttendance(
  baseline: Map<string, AttendanceSnapshot>,
  records: Array<{ userId: string } & AttendanceSnapshot>
): boolean {
  return records.some((r) => isAttendanceRowDirty(baseline.get(r.userId), r));
}

/**
 * AZ ALAPÉRTELMEZETT ESEMÉNY KIVÁLASZTÁSA [Web UX 3. csomag (B), 2026-10-04 — jelentés 1.3].
 *
 * MI VOLT A BAJ: a jelenléti ív a nap ELSŐ eseményét nyitotta (`setSelectedEvent(data[0])`), így a
 * 17:30-kor érkező edző a délelőtti edzés ívét kapta, és át kellett váltania — a legsűrűbb napi
 * műveletnél egy felesleges (és könnyen elnézhető) lépés.
 *
 * A SZABÁLY (sorrendben):
 *   1. a FOLYAMATBAN lévő esemény (kezdés ≤ most < vége);
 *   2. ha nincs: a legközelebb KEZDŐDŐ (jövőbeli) esemény;
 *   3. ha mind véget ért: a LEGKÉSŐBB kezdődő (a nap utolsó eseménye).
 *
 * TÖBB FOLYAMATBAN LÉVŐ ESEMÉNY (átfedés) — DÖNTÉS: a KÉSŐBB KEZDŐDÖTT nyílik. Indok: a jelenlétet
 * az edzés ELEJÉN veszik fel; ha 17:00–19:00 és 18:00–19:30 között 18:05-kor nyitja meg valaki az
 * ívet, a frissen indult edzés íve a valószínű cél — a korábbié jellemzően már fel van véve.
 * Egyenlő kezdésnél a lista sorrendje dönt (stabil, determinisztikus).
 *
 * IDŐ: a „most" a böngésző HELYI ideje (a klub gépei budapesti zónában futnak), és INJEKTÁLHATÓ,
 * hogy a teszt falióra-függetlenül mérhessen. A nap a `parseDateLocal`-lal jön (NEM
 * `new Date('yyyy-MM-dd')`, az UTC-éjfél — memória `utc-vs-local-day-bug-class`); a szerver
 * `"2026-10-04T00:00:00"` alakjából csak a dátum-rész számít (a `parseDateLocal` a „T…" farkat
 * nem tűri, ezért a `slice(0, 10)`). A helyi `Date(év, hó, nap, óra, perc)` konstruktor a
 * nyári/téli óraátállítást is a helyi idő szerint kezeli.
 * ÉJFÉLEN ÁTNYÚLÓ esemény (pl. 23:00–00:30): ha a vége nem későbbi a kezdésnél, a vége a KÖVETKEZŐ nap.
 * Értelmezhetetlen dátumú/idejű esemény a besorolásból kimarad; ha EGYIK sem értelmezhető, az
 * eredeti első elem a tartalék (ugyanaz, mint a régi viselkedés).
 */
export function pickDefaultEvent<T extends { eventDate: string; startTime: string; endTime: string }>(
  events: readonly T[],
  now: Date,
): T | null {
  if (events.length === 0) return null;
  const toMinutes = (t: string): number | null => {
    const m = /^(\d{1,2}):(\d{2})/.exec(t || '');
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const spans = events.map((e) => {
    const day = parseDateLocal(String(e.eventDate || '').slice(0, 10));
    const sm = toMinutes(e.startTime);
    const em = toMinutes(e.endTime);
    if (isNaN(day.getTime()) || sm === null || em === null) return { e, start: NaN, end: NaN };
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(sm / 60), sm % 60).getTime();
    // Éjfélen átnyúló: a vége a következő nap ugyanazon órája/perce.
    // ⚠️ VÉDEKEZŐ ÁG [1. validátor-kör F2, 2026-10-04]: a backend ma TILTJA a `vége ≤ kezdés`
    // eseményt (`EventController.cs:359` létrehozás, `:738` módosítás → 400, mögötte a DB
    // `CHK_Events_TimeRange` CHECK-constraintje), tehát ilyen esemény a szerverről ma nem jöhet.
    // Az ág azért marad, hogy egy jövőbeli éjszakai esemény (vagy régi, constraint előtti adat) ne
    // „véget ért" eseményként essen ki a folyamatban lévők közül. Őr:
    // `EventAttendance.pickDefaultEvent.test.ts` „ÉJFÉLEN ÁTNYÚLÓ" (megkülönböztető esetekkel).
    const endDayOffset = em <= sm ? 1 : 0;
    const end = new Date(day.getFullYear(), day.getMonth(), day.getDate() + endDayOffset, Math.floor(em / 60), em % 60).getTime();
    return { e, start, end };
  });
  const t = now.getTime();
  const valid = spans.filter(x => !isNaN(x.start));
  if (valid.length === 0) return events[0];

  // 1. Folyamatban: a később kezdődött nyer (egyenlőségnél az előbb listázott).
  const running = valid.filter(x => x.start <= t && t < x.end);
  if (running.length > 0) {
    return running.reduce((best, x) => (x.start > best.start ? x : best)).e;
  }
  // 2. A legközelebbi jövőbeli kezdés.
  const upcoming = valid.filter(x => x.start > t);
  if (upcoming.length > 0) {
    return upcoming.reduce((best, x) => (x.start < best.start ? x : best)).e;
  }
  // 3. Mind véget ért: a legkésőbb kezdődő.
  return valid.reduce((best, x) => (x.start > best.start ? x : best)).e;
}

/**
 * NÉV-ÖSSZEVETÉS A NÉVSZŰRŐHÖZ [Web UX 3. csomag (B), 2026-10-04]: kis-nagybetű ÉS ékezet-
 * érzéketlen részszöveg-egyezés („kovacs" → „Kovács Anna"). Csak MEGJELENÍTÉSI szűrő — a mentés,
 * a mentetlen-számláló és az „Összes jelen" mindig a TELJES névsoron dolgozik.
 * Az összevetési alak a KÖZÖS `utils/nameSearch.ts` `normalizeForSearch` [1. validátor-kör M6,
 * 2026-10-04] — korábban itt egy vele azonos, külön `normalizeNameForSearch` függvény élt.
 */
export function matchesNameFilter(name: string | undefined | null, filter: string): boolean {
  const f = normalizeForSearch(filter);
  if (!f) return true;
  return normalizeForSearch(name).includes(f);
}

/** E fölötti létszámnál jelenik meg a „Név keresése" mező (jelentés 1.3: 10 fő FELETT). */
export const ATTENDANCE_NAME_FILTER_THRESHOLD = 10;

/**
 * A NÉVSOR MEGJELENÍTÉSI SORRENDJE [Web UX 3. csomag (B), 2026-10-04 — jelentés 1.3].
 *
 * MI VOLT A BAJ: a névsor a szerver sorrendjében jött (`EventController.GetEventParticipants`
 * lekérdezésében nincs `OrderBy`, a kliens sem rendezett) — a gyakorlatban index-sorrend, vagyis
 * a GUID-ok sorrendje: egy 15 fős ívben az edzőnek keresgélnie kellett.
 *
 * A SZABÁLY: az EDZŐ-sor(ok) ELÖL (egymás közt is ábécérendben), utánuk mindenki más magyar
 * ábécérendben (`localeCompare(…, 'hu')`: „Cs" a „C" után, „Sz" az „S" után, „Zs" a „Z" után).
 * Ugyanez a mobil jelenléti ív szabálya (`mobile/app/events/[id]/attendance.tsx:246-247`) — a két
 * felület egyezik. Az edző-sor MAI helye: a lokális DB-ben mérve (2026-10-04, ugyanaz a
 * lekérdezés-alak) az edző-sorok a játékosok ELŐTT jöttek, de ez index-sorrend volt, nem
 * garancia; most garantált. Egyező névnél a `userId` dönt (stabil, a sorok nem ugrálnak).
 *
 * ⚠️ EZ NÉZET: az `attendanceRecords` ÁLLAPOT nem rendeződik át, a sorok kulcsa és minden módosító
 * `userId`-alapú, a mentés (`changedRecords`) és a mentetlen-számláló a teljes állapotból számol.
 */
export function sortAttendanceRowsForView<R extends { userId: string; userName?: string }>(
  records: readonly R[],
  isCoachRow: (userId: string) => boolean,
): R[] {
  return [...records].sort((a, b) =>
    Number(isCoachRow(b.userId)) - Number(isCoachRow(a.userId))
    || (a.userName || '').localeCompare(b.userName || '', 'hu')
    || (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0),
  );
}

/**
 * Van-e MEGÍRT, még el nem küldött megjegyzés [BL-4, 2026-09-13].
 *
 * MIÉRT a képernyő `isDirty`-jének RÉSZE: a megjegyzés-al-dialógus záró útját ugyan kapuzzuk
 * (`closeCommentDialog`), de a NEM-KATTINTÓS kilépés — F5, Ctrl+R, lapbezárás — csak a
 * `beforeunload`-on fogható, az pedig KIZÁRÓLAG akkor áll fel, ha a képernyő mentetlen adatot
 * JELENT BE. Mérve: beírt szöveggel a kapu „szabad" volt, és az F5 kérdés nélkül eldobta a
 * megírt megjegyzést — ugyanaz a hibaosztály, mint a jelenléti ív összeg-mezője volt.
 *
 * A korábbi kizárás indoka („egy nyitott al-dialógus nem blokkolhatja a fül-váltást") MÉRHETŐEN
 * alaptalan: amíg a MUI modális nyitva van, a mögötte lévő felület `aria-hidden`, tehát a
 * fül-gomb nem is elérhető — egy nem létező kockázat miatt maradt védelem nélkül a beírt szöveg.
 *
 * A `dialogOpen` feltétel a SZELLEM-PISZKOS állapot ellen van: a dialógus zárva is mountolva
 * marad, és bár mindkét záró út nullázza a szöveget (`closeCommentDialog`, `handleAddComment`),
 * így szerkezetileg is kizárt, hogy egy zárt dialógus mentetlen adatot jelentsen be.
 */
export function hasUnsavedCommentDraft(dialogOpen: boolean, draft: string): boolean {
  return dialogOpen && draft.trim().length > 0;
}

/**
 * A „Fizetett/Beszedett összeg" mező bevitele a KÖZÖS forint-szabályon él (`utils/money.ts`):
 * CSAK számjegy, EGÉSZ forint, előjel és tizedes-jel nélkül.
 *
 * ⚠️ ITT VOLT A NÉMA EZERSZERES TÉVEDÉS [BL-2, 2026-09-14]: a korábbi alak-szűrő szándékosan
 * elfogadta a pontot ÉS a magyar vesszőt tizedes-jelként, a parser pedig `Number`-rel értelmezte —
 * így a magyarul `2.800`-at (kétezer-nyolcszáz) gépelő felhasználó után a SZERVERRE 2,8 Ft ment ki,
 * miközben a mező változatlanul „2.800"-at mutatott. A `[Range(0.0, 1_000_000.0, …)]` átengedte, a
 * `decimal(10,2)` eltárolta, és a hibás összeg az edző kifizetéséből is levonásra került.
 * A mérést és a döntés (egész forint) indoklását lásd a `utils/money.ts` fejlécében.
 *
 * A korábbi, itt élt `PAYMENT_INPUT_SHAPE` + `parsePaymentAmount` páros helyére a közös
 * `isForintInputAccepted` + `parseForintInput` lépett — a felületen HAT külön parser élt ugyanerre a
 * feladatra, és mindegyik ugyanebbe a hibába futott [N-8].
 */

/**
 * A lap gyökerének belső margója telefonon (MUI spacing) — a telefonos sticky mentés-sáv ennyivel lóg
 * ki oldalra és lefelé, hogy a képernyő szélétől szélig, a menü fölött közvetlenül üljön [Web UX 9 / T3].
 */
const ATTENDANCE_ROOT_PADDING_XS = 2;

const EventAttendance: React.FC<EventAttendanceProps> = ({
  onNavigateToUserProfile,
  refreshTrigger,
  initialEventId,
  initialEventDate,
}) => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const { showSuccess, showInfo, showWarning, showError } = useFeedback();
  // isMobile (<600 px): telefonos tipográfia/érintési méretek. isSmallScreen (<900 px): a 8 oszlopos
  // táblázat tableten és fektetett telefonon is összezsúfolódik, ezért a kártyás elrendezés
  // már ott is jön — a „Jelenlét" oldalt sokan a telefon böngészőjéből használják. [klub-kérés]
  const { isMobile, isSmallScreen } = useResponsive();
  /**
   * A rögzített mentés-sáv (`renderSaveBar`) DOM-eleme [Web UX 9 / T3, 2026-10-13]: (1) a toast
   * „akadálya" (`useToastObstacle` — a visszajelzés a sáv FÖLÉ kerül), (2) a fókuszált mező
   * takarás-ellenőrzésének mércéje (`keepFocusedFieldAboveSaveBar`).
   */
  const saveBarRef = useRef<HTMLDivElement | null>(null);
  const saveBarToastObstacleRef = useToastObstacle();
  const setSaveBarRef = useCallback((el: HTMLDivElement | null) => {
    saveBarRef.current = el;
    saveBarToastObstacleRef(el);
  }, [saveBarToastObstacleRef]);
  /** A képernyő gyökere — a fókusz-figyelő csak a SAJÁT mezőkre hat, a portálos dialógusokra nem. */
  const attendanceRootRef = useRef<HTMLDivElement | null>(null);
  /**
   * Mutató (egér / érintés / toll) okozta-e a KÖVETKEZŐ fókuszt [Web UX 9 / T3, 2026-10-13, validátor B1].
   * A gyökér `pointerdown`/`mousedown`/`touchstart` (capture) állítja, a fókusz-figyelő elfogyasztja,
   * és BÁRMELY billentyű (document-szintű `keydown`, capture) törli — így a Tab-os fókusz mindig
   * „billentyűzetes", akkor is, ha előtte valahová (nem fókuszálható helyre) kattintottak.
   * MIÉRT NEM `:focus-visible`: a Chromium szöveges mezőn egérkattintásra is `:focus-visible`-t ad,
   * jelölőnél heurisztikus — a jelző determinisztikus, és jsdom-ban is mérhető.
   */
  const pointerFocusPendingRef = useRef(false);
  const markPointerFocus = useCallback(() => {
    pointerFocusPendingRef.current = true;
  }, []);
  useEffect(() => {
    const onKeyDown = () => {
      pointerFocusPendingRef.current = false;
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [comments, setComments] = useState<EventComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * BETÖLTÉSI hiba, ÚJRAPRÓBÁLKOZÁSSAL [BL-4, 2026-09-15]. Külön a `error`-tól, mert az a
   * MŰVELETEK (mentés, megjegyzés, résztvevő) hibáit mutatja, amikhez nincs mit újrapróbálni.
   * A `kind` azért kell, hogy a SIKERES újratöltés csak a SAJÁT hibáját takarítsa el.
   */
  const [loadError, setLoadError] = useState<{ kind: 'participants' | 'attendance'; message: string; retry: () => void } | null>(null);
  /**
   * Sikeresen betöltött-e a résztvevő-névsor. A jogosultság CSAK ebből dönthető el — üres névsor
   * és „nem tudjuk" nem ugyanaz (lásd `attendanceLockReason`).
   */
  const [participantsLoaded, setParticipantsLoaded] = useState(false);
  /**
   * ÚTON VAN-E MÉG A KIVÁLASZTOTT ESEMÉNY NÉVSORA [S-3, 2026-09-15].
   *
   * MI VOLT A BAJ (MÉRVE, és a korábbi komment tagadta): eseményváltáskor a névsor-lekérés
   * (`fetchEventParticipants`) NEM állítja a `loading`-ot, a jelenlét-lekérés
   * (`fetchAttendance`) igen — és a jelenlét-lekérés a sorokat a MEGLÉVŐ `users` névsorból
   * építi (a váltó effektus szándékosan nem üríti a `users`-t, lásd az S-7 megjegyzését).
   * Ezért ha a jelenléti válasz ELŐBB ért vissza, mint az új névsor, a `loading` lekapcsolt, és
   * a RÉGI esemény névsora jelent meg az ÚJ ív alatt — KAPCSOLHATÓ jelölőkkel, „Fizetett összeg"
   * mezőkkel. Néhány száz ms után magától javult (az új névsor felülírja), de addig egy gyors
   * kattintás egy IDEGEN esemény tagját jelölte volna jelenlévőnek.
   *
   * MIÉRT KÜLÖN ÁLLAPOT, és miért nem a `participantsLoaded`: az HIBA esetén SZÁNDÉKOSAN `false`
   * marad („nem tudjuk", nem „nincs jogod"), tehát rá kapuzva a pörgő ÖRÖKRE ott maradna egy
   * elbukott névsor-lekérés után — épp az „Újra" gombot takarva el. Ez a jelző a KÉRÉS
   * LEZÁRÁSÁT követi: sikerre és hibára is `false` lesz.
   */
  const [rosterPending, setRosterPending] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  /**
   * Az összeg-mezőkbe GÉPELT nyers szöveg (userId → szöveg) [NB-3, 2026-09-13].
   *
   * MIÉRT KELL: a mező kontrollált, az állapot viszont SZÁMOT tárol, és a szám-alak nem tud
   * visszaadni minden gépelt alakot (`"0"` után `"05"`, vezető nullák) — a `type="number"` input
   * pedig a nem értelmezhető tartalomra `''`-t ad, tehát a kontrollált mező KIÜRÜLT a felhasználó
   * szeme előtt (a régi, kontrollálatlan mezőnél ez nem történt — ez az első kör regressziója
   * volt). A nyers szöveg itt él a mentésig; a mentendő szám ettől függetlenül a soron van.
   * A tizedes-jeles alak (`"2800."`) 2026-09-14 óta nem is kerülhet a mezőbe [BL-2].
   */
  const [paymentDrafts, setPaymentDrafts] = useState<Map<string, string>>(new Map());
  // A dátumválasztó `new Date('YYYY-MM-DD')`-et állít be — ez UTC-éjfél, tehát a Date OBJEKTUM
  // helyi időben már az előző nap 23:00/22:00-ja lehet. KIÍRÁSKOR ezért NEM `toISOString()`
  // megy, hanem a HELYI napot adó `formatDateToLocal` (a `fetchEvents` szűrője és a „Dátum"
  // mező értéke is azon megy át) [NB-6, 2026-09-14] — a mezőbe érkező `YYYY-MM-DD` és a
  // szerverre menő nap így ugyanaz a nap marad.
  // ⚠️ A komment 2026-09-14-ig `toISOString()`-et állított: az már nem igaz, a csúszás javítva.
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    initialEventDate ? new Date(initialEventDate.split('T')[0]) : new Date()
  );
  /**
   * Az esemény ablakából érkező cél-esemény, amit a nap betöltése UTÁN kell kiválasztani.
   * Ref, nem state: a `fetchEvents` nem függhet tőle, különben körbe-újratöltést okozna.
   */
  const pendingEventIdRef = useRef<string | undefined>(initialEventId);
  /** Az utolsó elindított esemény-betöltés sorszáma — az elavult válaszok eldobásához. */
  const fetchSeqRef = useRef(0);
  /**
   * Ugyanez a JELENLÉT-betöltésre [review NB-7, javítva 2026-09-15]: a `fetchAttendance` eddig
   * őr NÉLKÜL írta ki az eredményét, holott eseményváltásnál (és a `refreshTrigger`-re) két
   * betöltés egymásra futhat — az elavult válasz így a FRISS esemény névsora fölé írhatta a
   * régi esemény sorait és alapvonalát.
   */
  const attendanceSeqRef = useRef(0);
  /**
   * SORSZÁM-ŐR A RÉSZTVEVŐ-NÉVSORRA [M3, 2026-09-15]. A `fetchAttendance` már kapott ilyet
   * (NB-7), a névsor-lekérés viszont NEM — pedig ugyanígy versenyeznek.
   *
   * A MÉRT FORGATÓKÖNYV: az „A" esemény névsora LASSAN bukik el, közben az edző „B"-re vált, „B"
   * névsora BETÖLT — majd „A" hibája befut, és a `fail()` kiüríti „B" névsorát, sőt az „Újra"
   * gomb „A" névsorát töltötte volna „B" alá. Az elavult futás eredményét (és hibáját is) ezért
   * el kell dobni.
   */
  const participantsSeqRef = useRef(0);
  /**
   * A MINDENKORI kiválasztott esemény — az „Újra" gomb ebből dolgozik, nem a hibát kiváltó
   * (esetleg már elhagyott) eseményből [M3].
   */
  const selectedEventRef = useRef<Event | null>(null);
  const [makeupCredits, setMakeupCredits] = useState<Map<string, number>>(new Map());
  /**
   * AKINEK A PÓTLÁSI KREDITJE NEM VOLT LEKÉRDEZHETŐ [B1, 2026-09-15].
   *
   * MI VOLT A BAJ (MÉRVE): a `fetchMakeupCredits` `catch`-e MINDEN hibára
   * `creditsMap.set(userId, 0)`-t hívott (csak a NAPLÓZÁS szűrt 404-re). A szerver 2026-09-15 óta
   * helyesen 500-at ad a belső hibára — egy ÁTMENETI adatbázis-hiba tehát „0 kredit"-ként
   * jelent meg: a „Pótlás" jelölő letiltva, a „N pótlás" címke sehol. Az edző ebből azt olvasta
   * ki, hogy a játékosnak NINCS kreditje, és KÉSZPÉNZT szedett be tőle — pontosan az a hiba,
   * amit a kredit-út javításával lezártnak hittünk.
   *
   * A DÖNTÉS (ugyanaz, mint a BL-4-nél): a „NEM TUDJUK" nem egyenlő a „NINCS"-csel.
   *  • 404 (nincs egyenleg-sora) → TOVÁBBRA IS 0 kredit, CSENDBEN: ez a szerver válasza, nem hiba.
   *  • MINDEN MÁS hiba (500, hálózat) → a kredit ISMERETLEN: a felhasználó SEM 0-t lát, sem
   *    számot, hanem „?"-et a magyarázattal; a „Pótlás" jelölő ZÁRVA marad (biztonságos irány:
   *    vaktában NEM adunk kreditet), a hiba pedig LÁTHATÓ és újrapróbálható.
   */
  const [creditsFailed, setCreditsFailed] = useState<Set<string>>(new Set());
  /** A `creditsFailed` szinkron tükre — lásd a `fetchMakeupCredits` végén az indoklást. */
  const creditsFailedRef = useRef<Set<string>>(new Set());
  /**
   * A MENTÉST BLOKKOLÓ PÉNZ-HIBA A MEZŐN [M2, 2026-09-15].
   *
   * MI VOLT A BAJ: a köztes összeg-vázlat őre (BL-2b) csak `setError`-t hívott, ami a
   * táblázat/kártya TETEJE fölötti Alertbe megy. 15+ fős névsornál az edző LENT görget, tehát a
   * Mentés gomb megnyomása után SEMMIT nem látott: a mentés NÉMA no-op lett. (Ugyanez a csapda
   * négy helyen élt — a kód maga is kimondja a `:2138` kommentben, hogy a felső sáv hosszú
   * űrlapon nem látszik.) Most a hiba HÁROM helyen jelenik meg: a felső sáv, egy TOAST, és
   * maga a MEZŐ (piros keret + súgó), amit oda is GÖRGETÜNK és fókuszálunk.
   */
  const [paymentFieldError, setPaymentFieldError] = useState<{ userId: string; message: string } | null>(null);
  /** A pénz-mezők DOM-elemei (fókusz + görgetés a mentés-őrből) — sor-azonosító szerint. */
  const paymentInputsRef = useRef<Map<string, HTMLInputElement>>(new Map());
  /**
   * A kredit-lekérés hibájának MAGYAR szövege + újrapróbálás.
   *
   * MIÉRT KÜLÖN, és nem a `loadError`-ban [döntés, 2026-09-15]: a `loadError` EGY résre dolgozik
   * (`kind` szerint takarítja magát), és a névsor/jelenlét hibája FONTOSABB — ha a kredit-hiba
   * beülne ugyanabba a rekeszbe, kiszorítaná a zárolást magyarázó mondatot. A siker-visszajelzés
   * toast, a HIBA viszont MARADÓ sáv: a kredit-kérdés pénzről dönt, nem tűnhet el 6 másodperc
   * után. ⚠️ A toast EMELLETT is elmegy (`showWarning`), mert 15+ fős névsornál az edző legörgetve
   * a lap tetején lévő sávot nem látja — ez a mért csapda vitte félre a mentés-őröket is (M2).
   */
  const [creditsError, setCreditsError] = useState<{ message: string; retry: () => void } | null>(null);
  const [accordionExpanded, setAccordionExpanded] = useState(true);

  // Participant management states
  const [addParticipantDialogOpen, setAddParticipantDialogOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableRoles, setAvailableRoles] = useState<UserRole[]>([]);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState<User | null>(null);
  const [selectedRoleForNewParticipant, setSelectedRoleForNewParticipant] = useState<string>('');
  const [addedParticipantIds, setAddedParticipantIds] = useState<Set<string>>(new Set());
  // Betöltéskori pillanatkép a jelenlét-értékekről — mentéskor CSAK az ettől eltérő (vagy újonnan
  // hozzáadott) sorokat küldjük, hogy egy társ-edző mentése ne írja felül az érintetlen sorokat.
  const baselineRef = useRef<Map<string, { isPresent: boolean; hasPaidOnSite: boolean; isMakeup: boolean; paymentAmount?: number; presentFrom?: string | null; presentUntil?: string | null }>>(new Map());
  /** A mindenkori sorok ref-ben is — a `fetchAttendance` closure-je `users`-re van kötve, a friss helyi jelöléseket innen olvassa. */
  const attendanceRecordsRef = useRef<AttendanceRecord[]>([]);
  const [addingParticipant, setAddingParticipant] = useState(false);
  /**
   * A „Résztvevő hozzáadása” dialógus betöltési hibája [BL-4, 2026-09-15]. Külön állapot, mert a
   * fő hiba-sáv a dialógus MÖGÖTT van (mobilon teljes képernyős a dialógus), tehát ott nem látszana.
   */
  const [addParticipantError, setAddParticipantError] = useState<string | null>(null);
  /**
   * [7. javító kör — validátor F3, 2026-10-04] Ugyanez az elv a dialógusok MENTÉSI hibájára: a
   * résztvevő HOZZÁADÁSÁNAK és a megjegyzés mentésének hibája korábban csak a fő hiba-sávba került
   * (a nyitva maradó dialógus mögé) — a felhasználó semmit nem látott.
   */
  const [addSaveError, setAddSaveError] = useState<string | null>(null);
  const [commentError, setCommentError] = useState<string | null>(null);

  const isAdmin = user?.userRole === 'Administrator';
  const isCoach = user?.userRole === 'Coach';
  const canEdit = isAdmin || isCoach;

  // Check if coach is participant in the selected event
  const isCoachParticipant = selectedEvent && users.some(u => u.userId === user?.userId);

  // Résztvevő-kezelés (hozzáadás/eltávolítás) jogosultsága. Korábban minden vezérlő
  // a "canEditEvent && isCoachParticipant" feltételre volt kötve → az admin (aki
  // jellemzően NINCS a résztvevők közt) sosem látta a hozzáadás gombot / törlés-ikonokat.
  // Az admin mindig kezelhet; a coach csak akkor, ha résztvevője/hozzárendeltje az eseménynek.
  const canManageParticipants = computeCanManageParticipants(isAdmin, isCoach, !!isCoachParticipant);

  /**
   * MENTETLEN-VÁLTOZÁS VÉDELEM [2026-09-13]: a képernyő csak BEJELENTI, hogy van-e mentetlen adata,
   * a kilépési pontok (Dashboard fül-váltás, lap bezárása) ebből kérdezik meg, szabad-e továbbmenni.
   * Az alapvonal a BETÖLTÉS UTÁN áll fel, ezért a betöltés közbeni állapot nem számít piszkosnak.
   */
  const commentDraftDirty = hasUnsavedCommentDraft(commentDialogOpen, newComment);
  const isDirty = hasUnsavedAttendance(baselineRef.current, attendanceRecords) || commentDraftDirty;
  useUnsavedChanges(isDirty, 'Jelenléti ív');

  /**
   * NÉZET A NÉVSORRA [Web UX 3. csomag (B), 2026-10-04 — jelentés 1.3]: rendezés (edző elöl, majd
   * magyar ábécé, `sortAttendanceRowsForView`) + 10 fő FELETT névszűrő. CSAK MEGJELENÍTÉS:
   *  · az `attendanceRecords` állapot sorrendje NEM változik, a módosítók `userId`-alapúak;
   *  · a mentés (`changedRecords`) az ÖSSZES sorból számol — a kiszűrt, de módosított sor is megy;
   *  · a mentetlen-számláló (`unsavedRowCount`) és a kilépési kérdés (`isDirty`) az ÖSSZES sort látja;
   *  · az „Összes jelen" (`handleMarkAllPresent`) szűrt nézetben is MINDENKIT jelöl (alapértelmezés,
   *    a csomag-prompt 0.1/1.3 szerint) — ezt a szűrő segédszövege a felületen is kimondja.
   */
  const [nameFilter, setNameFilter] = useState('');
  const sortedAttendanceRecords = useMemo(() => {
    const roleOf = new Map(users.map(u => [u.userId, u]));
    return sortAttendanceRowsForView(attendanceRecords, (userId) => {
      const u = roleOf.get(userId);
      return isCoachRole(u?.userRoleSystemName, u?.userRoleName);
    });
  }, [attendanceRecords, users]);
  const showNameFilter = attendanceRecords.length > ATTENDANCE_NAME_FILTER_THRESHOLD;
  // Ha a névsor a küszöb alá csökken (résztvevő eltávolítása), a rejtett mező szűrője nem hathat.
  const activeNameFilter = showNameFilter ? nameFilter : '';
  const visibleAttendanceRecords = useMemo(
    () => (activeNameFilter.trim()
      ? sortedAttendanceRecords.filter(r => matchesNameFilter(r.userName, activeNameFilter))
      : sortedAttendanceRecords),
    [sortedAttendanceRecords, activeNameFilter],
  );
  const isNameFiltered = activeNameFilter.trim().length > 0;
  /**
   * A KÜSZÖB ALÁ CSÖKKENT NÉVSOR A SZŰRŐ SZÖVEGÉT IS ÜRÍTI [1. validátor-kör F4, 2026-10-04].
   * DÖNTÉS: nem elég, hogy a rejtett mező szűrője nem hat (`activeNameFilter`, ez védi az első
   * renderelést); a szövegnek el is kell tűnnie. Különben egy eltávolítás (11 → 10 fő) után a
   * mező eltűnik, majd egy újabb walk-in felvételénél (10 → 11) a RÉGI keresőszó láthatatlan
   * előzmény nélkül újra szűrne, és sorok tűnnének el a felhasználó szeme elől.
   * Őr: `EventAttendance.sortFilterSaveBar.test.tsx` „küszöb alá csökken".
   */
  useEffect(() => {
    if (!showNameFilter && nameFilter !== '') setNameFilter('');
  }, [showNameFilter, nameFilter]);
  /**
   * A SZŰRŐ MIATT REJTETT HIBÁS PÉNZ-MEZŐ FÓKUSZA [1. validátor-kör F5, 2026-10-04].
   * A `reportPaymentFieldError` ilyenkor üríti a szűrőt és ide jegyzi a sort; a fókusz/görgetés
   * a renderelés UTÁN fut (az effekt a commit után jön, addigra a mező `inputRef`-je bejegyezte
   * magát a `paymentInputsRef`-be). Lásd `reportPaymentFieldError`.
   */
  const pendingPaymentFocusRef = useRef<string | null>(null);
  useEffect(() => {
    const userId = pendingPaymentFocusRef.current;
    if (!userId) return;
    // Egyszer használatos: akkor is elengedjük, ha a mező nincs meg (ne fókuszáljon később váratlanul).
    pendingPaymentFocusRef.current = null;
    const input = paymentInputsRef.current.get(userId);
    input?.scrollIntoView?.({ block: 'center' });
    input?.focus?.();
  }, [visibleAttendanceRecords]);
  /**
   * A mentetlen SOROK száma a rögzített mentés-sávhoz — UGYANAZ a forrás (`computeDirtyUserIds` ↔
   * `hasUnsavedAttendance`, mindkettő `isAttendanceRowDirty`), mint a kilépési kérdésé, és az
   * ÖSSZES soron (a szűrő által elrejtetteken is) számol.
   */
  const unsavedRowCount = computeDirtyUserIds(baselineRef.current, attendanceRecords).length;

  /**
   * A KÉPERNYŐN BELÜLI váltások (dátum, esemény-választó) nem mennek a Dashboardon át, ezért itt
   * kérdezünk rá: mindkettő eldobja a még nem mentett jelöléseket (eseményváltáskor a helyi sorok
   * és az alapvonal is nullázódik). Mégse esetén a dátum/esemény NEM változik.
   */
  const confirmDiscardUnsaved = async (): Promise<boolean> => {
    if (!isDirty) return true;
    return confirm({
      title: 'Mentetlen változás',
      message: 'Mentetlen változásod van a jelenléti íven. Ha váltasz, a beírt adatok elvesznek.',
      confirmLabel: 'Váltás',
      cancelLabel: 'Mégse',
      // A megerősítés ELDOBJA a beírt adatot, ezért vörös — a `formDirty.discardChangesConfirm`
      // kanonikus döntésével egyezően (koordinátor, 2026-09-13).
      destructive: true,
    });
  };

  // Calculate real-time statistics from attendanceRecords
  const presentCount = attendanceRecords.filter(record => record.isPresent).length;
  const paidCount = attendanceRecords.filter(record => record.hasPaidOnSite).length;
  const totalParticipants = attendanceRecords.length;
  const attendancePercentage = totalParticipants > 0 ? Math.round((presentCount / totalParticipants) * 100) : 0;

  /**
   * Szerep-térkép a résztvevőkből: userId → { gépi név, megjelenített név }.
   *
   * ⚠️ MI VOLT ITT [B-W4, 2026-09-16]: `isCoachRole = roleName === 'Edző'`, vagyis a jelenléti ív
   * a MEGJELENÍTETT szerepkör-névre épített GÉPI döntést. Az admin a szerepkör „Edző" címkéjét
   * ÁTNEVEZHETI — attól a pillanattól az edző sora játékosként viselkedett volna (más címke:
   * „fizetett" a „beszedett" helyett; és a saját-edző felismerése, `:1466`, is elcsúszott volna).
   * Most a döntés a szerver GÉPI neveből megy (`userRoleSystemName`), a régi alakon pedig a
   * `utils/roles.ts` a megjelenített névre esik vissza — így a viselkedés nem változik ott sem.
   */
  const roleByUserId = new Map<string, { system?: string; display?: string }>(
    users.map(u => [u.userId, { system: u.userRoleSystemName, display: u.userRoleName }])
  );
  // Sor-függő címke: az edző BESZED, a játékos FIZET
  const isCoachRow = (userId: string) => {
    const role = roleByUserId.get(userId);
    return isCoachRole(role?.system, role?.display);
  };

  // Beszedések a rögzítő (RecordedBy) szerint csoportosítva — EZ ALAPJÁN történik a levonás:
  // egy edző payoutjából csak az vonódik le, amit ő rögzített. Admin által rögzített beszedés
  // egyik edzőtől sem vonódik le.
  const collectionByRecorder = (() => {
    const m = new Map<string, { name: string; amount: number; isCoach: boolean }>();
    attendanceRecords.forEach(r => {
      // A panel már akkor megjelenik, ha van bepipált beszedés (összeg nélkül is) — az összeg
      // 0-ról frissül, ahogy beírják. Így a magyarázó és a kimutatás azonnal látszik.
      if (r.hasPaidOnSite && r.recordedBy) {
        const prev = m.get(r.recordedBy);
        m.set(r.recordedBy, {
          name: r.recordedByName || '—',
          amount: (prev?.amount || 0) + (r.paymentAmount || 0),
          // Ugyanaz a döntés, mint a sor-címkénél: GÉPI név, display-tartalékkal [B-W4].
          isCoach: isCoachRow(r.recordedBy),
        });
      }
    });
    return Array.from(m.values()).sort((a, b) => b.amount - a.amount);
  })();
  const coachCollections = collectionByRecorder.filter(c => c.isCoach);
  const nonCoachCollections = collectionByRecorder.filter(c => !c.isCoach);
  const hasAnyCollection = collectionByRecorder.length > 0;
  // A PÉNZ-KIÍRÁS EGY KÖZÖS SEGÉDEN [B-W3, 2026-09-16]: itt korábban SAJÁT formázó állt
  // (`${n.toLocaleString('hu-HU')} Ft`), a kimutatásokban pedig egy harmadik alak — ugyanarra az
  // összegre két-három különböző írásmód. A `formatForint` (`utils/money.ts`) egyetlen alakot ad,
  // és a csoport-elválasztót is EGY karakterre normalizálja (az ICU-verziók eltérnek benne).
  const formatHuf = (n: number) => formatForint(n);

  /**
   * Egyetlen esemény lekérése azonosító alapján. Csak arra az esetre, amikor az esemény
   * ablakából egy konkrét eseményre nyitunk, de az a napi listából kimaradt.
   */
  const fetchSingleEvent = async (eventId: string): Promise<Event | null> => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/events/${eventId}`);
      if (!response.ok) return null;
      const result = await response.json();
      return (result.data || result) as Event;
    } catch (err) {
      console.error('Error loading target event:', err);
      return null;
    }
  };

  const fetchEvents = useCallback(async () => {
    // Kérés-sorszám: a StrictMode fejlesztés alatt KÉTSZER futtatja az effektust, és éles
    // buildben is előfordulhat két egymásra futó betöltés (pl. gyors dátumváltás). Az elavult
    // válasz eredményét el kell dobni, különben felülírja a frisset — így nyílt korábban a
    // nap első eseménye a célzott helyett.
    const seq = ++fetchSeqRef.current;
    const isStale = () => seq !== fetchSeqRef.current;

    setLoading(true);
    // A korábbi betöltés hibáját töröljük: különben egy sikeres újratöltés (pl. dátumváltás
    // után) a helyes lista fölött hagyná ott a piros sávot.
    setError(null);
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      // Add date filter if selected
      if (selectedDate) {
        // HELYI nap, nem UTC [NB-6, 2026-09-14]: a `toISOString()` 00:00–02:00 budapesti idő
        // között a TEGNAPI napot adja, tehát az ív éjfél után a TEGNAPI eseményeket töltötte
        // (a naptár ugyanezt helyi időben számolja — a kettő szétcsúszott). MÉRVE: az E2E
        // suite 00:00 után nem találta a mai eseményt a választóban.
        const selectedDateStr = formatDateToLocal(selectedDate);
        queryParams.append('startDate', selectedDateStr);
        queryParams.append('endDate', selectedDateStr);
      }

      // For coaches, only show events where they participate as coach
      if (isCoach && user?.userId) {
        queryParams.append('userId', user.userId);
      }

      const queryString = queryParams.toString();
      const url = queryString ? `${API_BASE_URL}/api/events?${queryString}` : `${API_BASE_URL}/api/events`;

      const response = await authFetch(url);

      // [Web UX 3. csomag (D), 2026-10-04] a `!ok` válasz a szerver magyar indoklását / „<tartalék> (HTTP n)"-t adja (eddig egy
      // angol `throw new Error('Failed to …')` után a catch FIX szövege ment ki, az indoklás elveszett).
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        // Elavult kérés hibáját itt sem írjuk ki (lásd a catch-ágat).
        if (!isStale()) setError(requestResponseErrorMessage(body, response.status, 'Nem sikerült betölteni az eseményeket'));
        return;
      }

      const result = await response.json();
      const rawData = result.data || result; // Handle both {success:true, data:[...]} and direct array responses

      // Filter out TableReservation events - no attendance tracking needed
      // (a rendszer-név `eventTypeName` alapján, NEM a felhasználónak szánt `eventTypeDisplayName`)
      const data = rawData.filter((e: any) => e.eventTypeName !== 'TableReservation');

      if (isStale()) return;

      // Ha az esemény ablakából érkeztünk, ARRA az eseményre nyitunk, nem a nap elsőjére.
      // A célt SZÁNDÉKOSAN csak sikeres kiválasztás után felejtjük el: ha az `await` előtt
      // ürítenénk, egy párhuzamos (vagy elbukó) betöltés „elfogyasztaná”, és a másik futás a
      // nap első eseményére nyitna.
      const targetId = pendingEventIdRef.current;
      if (targetId) {
        const found = data.find((e: any) => e.eventId === targetId);
        if (found) {
          pendingEventIdRef.current = undefined;
          setEvents(data);
          setSelectedEvent(found);
          return;
        }

        // Edzőnél a napi lista CSAK a saját eseményeit tartalmazza (lásd a `userId` szűrőt
        // fentebb), ezért a cél-esemény hiányozhat belőle. Ilyenkor külön lekérjük — a gomb
        // nem vihet üres képernyőre.
        const single = await fetchSingleEvent(targetId);
        if (isStale()) return;
        if (single) {
          pendingEventIdRef.current = undefined;
          setEvents([single, ...data]);
          setSelectedEvent(single);
          return;
        }

        // Sehogy sem érhető el: elfelejtjük, különben beragadna, és egy későbbi dátumváltásnál
        // egy MÁS napi eseményt húzna be a friss választás helyére.
        pendingEventIdRef.current = undefined;
      }

      setEvents(data);

      // ALAPÉRTELMEZETT ESEMÉNY [Web UX 3. csomag (B), 2026-10-04]: a folyamatban lévő / a
      // legközelebb kezdődő / a nap utolsó eseménye — NEM a nap első eseménye (`data[0]`), lásd
      // `pickDefaultEvent`. A cél-esemény (fentebb, `pendingEventIdRef`) ELSŐBBSÉGE változatlan:
      // ide csak akkor jutunk, ha nem volt cél, vagy az sehogy sem volt elérhető.
      const defaultEvent = pickDefaultEvent<Event>(data, new Date());
      if (defaultEvent) {
        setSelectedEvent(defaultEvent);
      }
    } catch (err) {
      console.error(err);
      // Elavult kérés hibáját NEM írjuk ki: a friss betöltés lehet, hogy épp sikerült, és a
      // piros sáv ott maradna felette. (StrictMode alatt az első futás bukása ilyen.)
      if (!isStale()) setError('Nem sikerült betölteni az eseményeket');
    } finally {
      // Ugyanezért a pörgő sem állítható le elavult futásból: villanást okozna, miközben a
      // friss kérés még úton van.
      if (!isStale()) setLoading(false);
    }
  }, [selectedDate, isCoach, user?.userId]);

  /**
   * A résztvevő-névsor betöltése — a HIBA LÁTHATÓ, és újrapróbálható [BL-4, 2026-09-15].
   *
   * A hibaszöveg MINDIG magyar: a szerver saját indoklása (`responseErrorMessage`), vagy — ha a
   * kérés el sem érte a szervert — a közös hálózati mondat. ⚠️ A `fetch` hálózati bukása sima,
   * ANGOL `TypeError('Failed to fetch')`, azt nem írhatjuk ki a végig magyar felületre (ezt a
   * csapdát az `utils/apiErrors.ts` fejléce is kimondja), ezért a három ág külön van kezelve.
   *
   * A típus-annotáció SZÁNDÉKOS: a `retry` önmagát hívja, és annotáció nélkül a TypeScript a
   * `useCallback` visszatérési típusát nem tudná kikövetkeztetni („referenced in its own initializer").
   */
  const fetchEventParticipants: (eventId: string) => Promise<void> = useCallback(async (eventId: string) => {
    // SORSZÁM-ŐR [M3, 2026-09-15]: az elavult futás SEM az eredményét, SEM a hibáját nem írhatja
    // a friss esemény fölé (a részletes forgatókönyv a `participantsSeqRef` doksijában).
    const seq = ++participantsSeqRef.current;
    const isStale = () => seq !== participantsSeqRef.current;
    const fallback = 'Nem sikerült betölteni a résztvevőket';
    const fail = (message: string) => {
      if (isStale()) return;
      setUsers([]);
      // ⚠️ A jogosultság NEM dőlhet el egy elbukott lekérésből: „nem tudjuk" ≠ „nincs jogod".
      setParticipantsLoaded(false);
      // A kérés LEZÁRULT (hibával) — a pörgő nem maradhat a piros sáv és az „Újra" fölött [S-3].
      setRosterPending(false);
      setLoadError({
        kind: 'participants',
        message,
        // Az „Újra" a MINDENKORI kiválasztott eseményt tölti — enélkül a régi esemény névsorát
        // pakolta volna az új esemény alá.
        // ⚠️ EZ TUDATOSAN VÉDELMI TARTALÉK, nem önállóan mérhető ág [kimondva 2026-09-15]: az
        // eseményváltó effektus a `loadError`-t NULLÁZZA, tehát a felületen ma nem lehet a RÉGI
        // esemény „Újra" gombját megnyomni az ÚJ esemény kiválasztása után. Ha a nullázás
        // bármikor kikerülne, ez a sor akkor sem engedi, hogy a régi névsor az új alá kerüljön.
        retry: () => {
          const current = selectedEventRef.current;
          void fetchEventParticipants(current ? current.eventId : eventId);
        },
      });
    };

    let response: Response;
    try {
      response = await authFetch(`${API_BASE_URL}/api/events/${eventId}/participants`);
    } catch (err) {
      console.error('Failed to fetch event participants (network):', err);
      fail(NETWORK_ERROR_MESSAGE);
      return;
    }

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      console.error('Failed to fetch event participants:', response.status, body);
      fail(requestResponseErrorMessage(body, response.status, fallback));
      return;
    }

    try {
      const participants = await response.json();
      // Az `await`-ek alatt eseményt lehetett váltani — az elavult névsor nem írható ki [M3].
      if (isStale()) return;
      // Convert participant data to user format for compatibility
      const users = participants.map((p: any) => ({
        userId: p.userId,
        name: p.userName,
        email: p.userEmail || '', // Handle potential missing email
        userRoleName: p.userRoleName, // "Edző" / "Játékos" / "Adminisztrátor" — KIÍRÁSHOZ
        // A GÉPI név a szerepkör-döntésekhez [B-W4, 2026-09-16] — lásd `roleByUserId`.
        userRoleSystemName: p.userRoleSystemName,
        privateLessonFee: p.privateLessonFee ?? undefined, // csak magánórán + saját edzőnek/adminnak jön nem-null értékkel
      }));
      setUsers(users);
      setParticipantsLoaded(true);
      // Az ÚJ esemény névsora megjött: az ív kirajzolható [S-3].
      setRosterPending(false);
      // Csak a SAJÁT hibáját takarítja el (a jelenlét-lekérés hibája maradjon látható).
      setLoadError(prev => (prev?.kind === 'participants' ? null : prev));
    } catch (err) {
      console.error('Failed to parse event participants:', err);
      fail(fallback);
    }
  }, []);

  const fetchAttendance: (eventId: string) => Promise<void> = useCallback(async (eventId: string) => {
    // SORSZÁM-ŐR [NB-7]: az elavult futás eredménye nem írhatja felül a frissét.
    const seq = ++attendanceSeqRef.current;
    const isStale = () => seq !== attendanceSeqRef.current;
    const fallback = 'Nem sikerült betölteni a jelenléti adatokat';
    // A betöltési hiba LÁTHATÓ és ÚJRAPRÓBÁLHATÓ [BL-4] — eddig csak egy szöveg volt, gomb nélkül.
    const fail = (message: string) => {
      if (isStale()) return;
      setLoadError({ kind: 'attendance', message, retry: () => { void fetchAttendance(eventId); } });
    };

    setLoading(true);
    try {
      let response: Response;
      try {
        response = await authFetch(`${API_BASE_URL}/api/attendance/event/${eventId}`);
      } catch (err) {
        // A kérés el sem érte a szervert: a `fetch` ANGOL TypeError-t dob, a felület magyar.
        console.error('Failed to fetch attendance (network):', err);
        fail(NETWORK_ERROR_MESSAGE);
        return;
      }

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        console.error('Failed to fetch attendance:', response.status, body);
        fail(requestResponseErrorMessage(body, response.status, fallback));
        return;
      }

      const data = await response.json();

      // Fetch unread notes counts for all users (only for coaches/admins)
      const unreadCounts = new Map<string, number>();
      if (canEdit) {
        await Promise.all(users.map(async (user) => {
          try {
            const count = await userNoteService.getUnreadNotesCount(user.userId);
            if (count > 0) {
              unreadCounts.set(user.userId, count);
            }
          } catch (err: any) {
            // Silently handle 404 errors (user has no notes)
            if (err?.response?.status !== 404 && err?.status !== 404) {
              console.error(`Failed to fetch unread notes count for user ${user.userId}:`, err);
            }
          }
        }));
      }

      // A jegyzet-számok lekérése is `await` — közben eseményt lehetett váltani [NB-7].
      if (isStale()) return;

      // Create attendance records for all users
      const records: AttendanceRecord[] = users.map(user => {
        const existing = data.attendances?.find((a: any) => a.userId === user.userId);
        const baseRecord = existing ? {
          ...existing,
          hasPaidOnSite: existing.hasPaidOnSite || existing.hasPaid || false, // Handle both old and new field names
          isMakeup: existing.isMakeup || false,
          // `?? undefined`, NEM `|| undefined` [N-18, 2026-09-14]: a szerveren tárolt 0 Ft ÉRDEMI
          // érték („ingyen jött be"), a `||` viszont „nincs megadva"-ként hozta be, tehát a mező
          // ÜRESEN mutatta — szemben a kód saját szabályával (üres = nincs megadva, 0 = ingyen).
          paymentAmount: existing.paymentAmount ?? undefined
        } : {
          userId: user.userId,
          userName: user.name,
          userEmail: user.email,
          isPresent: false,
          hasPaidOnSite: false,
          isMakeup: false,
          paymentAmount: undefined,
        };

        return {
          ...baseRecord,
          unreadNotesCount: unreadCounts.get(user.userId) || 0,
          privateLessonFee: user.privateLessonFee, // a névsorból; csak magánórán + saját edzőnek/adminnak nem-null
        };
      });

      // ADATVESZTÉS-VÉDELEM [Web UX A3, 2026-09-12]: ez a betöltő a `users` REFERENCIÁJÁRA fut újra
      // (walk-in résztvevő felvétele/eltávolítása, profil-dialógus bezárása utáni refreshTrigger), és
      // eddig a szerver állapotával FELÜLÍRTA a még nem mentett jelöléseket — pont a leggyakoribb edzői
      // művelet közben. Most a baseline-tól eltérő (piszkos) sorok helyi értékei megmaradnak, csak a
      // nem érintett sorok frissülnek a szerverről; a piszkos sorok baseline-ja sem változik.
      // A mező-összevetés a KÖZÖS `isAttendanceRowDirty`-vel (egy forrás: a betöltő, a mentés-diff és
      // a mentetlen-változás bejelentése ugyanazt a hat mezőt ugyanúgy hasonlítja).
      const localByUser = new Map(attendanceRecordsRef.current.map(r => [r.userId, r]));
      const isDirtyLocally = (userId: string) =>
        isAttendanceRowDirty(baselineRef.current.get(userId), localByUser.get(userId));
      const merged: AttendanceRecord[] = records.map(r => {
        if (!isDirtyLocally(r.userId)) return r;
        const local = localByUser.get(r.userId)!;
        return { ...r, isPresent: local.isPresent, hasPaidOnSite: local.hasPaidOnSite, isMakeup: local.isMakeup,
          paymentAmount: local.paymentAmount, presentFrom: local.presentFrom, presentUntil: local.presentUntil };
      });
      setAttendanceRecords(merged);
      // A gépelt nyers összeg-szövegekből CSAK a piszkos sorokét tartjuk meg [NB-3]: azoknak a
      // helyi értéke szándékosan megmarad (lásd fentebb), a többi sornál viszont a szerver értéke
      // az igazság — és az eltávolított résztvevők szövege sem maradhat ott.
      const draftsToKeep = new Set(records.filter(r => isDirtyLocally(r.userId)).map(r => r.userId));
      setPaymentDrafts(prev => {
        if (prev.size === 0) return prev;
        const next = new Map<string, string>();
        prev.forEach((draft, userId) => {
          if (draftsToKeep.has(userId)) next.set(userId, draft);
        });
        return next.size === prev.size ? prev : next;
      });
      // Baseline rögzítése a betöltött állapotról (a mentés diff-eléséhez) — a piszkos sorok baseline-ja marad
      const nextBaseline = new Map(baselineRef.current);
      records.forEach(r => {
        if (isDirtyLocally(r.userId)) return;
        nextBaseline.set(r.userId, {
          isPresent: r.isPresent, hasPaidOnSite: r.hasPaidOnSite, isMakeup: r.isMakeup, paymentAmount: r.paymentAmount,
          presentFrom: r.presentFrom ?? null, presentUntil: r.presentUntil ?? null,
        });
      });
      // az eltávolított résztvevők baseline-ja is menjen
      const currentIds = new Set(records.map(r => r.userId));
      Array.from(nextBaseline.keys()).forEach(k => { if (!currentIds.has(k)) nextBaseline.delete(k); });
      baselineRef.current = nextBaseline;

      // Fetch makeup credits for all users upfront so coaches see them immediately
      const allUserIds = users.map(u => u.userId);
      if (allUserIds.length > 0 && canEdit) {
        fetchMakeupCredits(allUserIds);
      }

      // Sikeres betöltés: csak a SAJÁT hibáját takarítja el (a névsor hibája maradjon látható).
      setLoadError(prev => (prev?.kind === 'attendance' ? null : prev));
    } catch (err) {
      console.error(err);
      fail(fallback);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [users]);

  useEffect(() => {
    attendanceRecordsRef.current = attendanceRecords;
  }, [attendanceRecords]);

  // Fetch events (today's events for coaches, all for admins)
  useEffect(() => {
    fetchEvents();
  }, [selectedDate]); // Remove fetchEvents from dependency

  // Fetch participants when event is selected
  useEffect(() => {
    // ESEMÉNYVÁLTÁS: a piszkos-sor megőrzés (A3) CSAK ugyanazon eseményen belül érvényes — a régi
    // esemény mentetlen jelölései nem vihetők át az újra (ugyanaz a játékos mindkettőn szerepelhet).
    // Ezért itt a helyi sorokat és a baseline-t is nullázzuk, mielőtt az új névsor betöltődik.
    baselineRef.current = new Map();
    attendanceRecordsRef.current = [];
    setAttendanceRecords([]);
    // A gépelt összeg-szövegek is eseményhez kötöttek (NB-3).
    setPaymentDrafts(new Map());
    // A walk-in „mindig küldendő" halmaza is eseményhez kötött (validátor NB-6).
    setAddedParticipantIds(new Set());
    // A névszűrő is eseményhez kötött [Web UX 3. csomag (B), 2026-10-04]: egy másik esemény
    // névsorán a régi keresőszó némán elrejthetne sorokat.
    setNameFilter('');
    // A névsor az ÚJ eseményre még nincs betöltve — a jogosultság addig „nem tudjuk" [BL-4].
    setParticipantsLoaded(false);
    /**
     * A BETÖLTÉSI HIBA IS ESEMÉNYHEZ KÖTÖTT [M3, 2026-09-15]. Enélkül a régi esemény piros sávja
     * (és a rajta lévő „Újra") ott maradt az ÚJ esemény fölött — az „Újra" pedig a RÉGI esemény
     * névsorát töltötte volna be alá.
     */
    setLoadError(null);
    /**
     * A KREDIT-ÁLLAPOT IS [B1]: a „?" jelvény és a figyelmeztető sáv a régi névsorra szólt.
     * A ref-et is nullázni kell, különben a következő sikeres lekérés nem tudná eltüntetni a sávot.
     */
    setMakeupCredits(new Map());
    setCreditsFailed(new Set());
    creditsFailedRef.current = new Set();
    setCreditsError(null);
    // Az elavult, MÉG FUTÓ névsor-lekérés eredményét is el kell dobni, nem csak a következőt
    // megelőzni — ezért itt is léptetjük a sorszámot [M3].
    participantsSeqRef.current += 1;
    /**
     * A JELENLÉT-LEKÉRÉS SORSZÁMA IS, TARTALÉKBÓL [S-7, 2026-09-15].
     * MA a lenti `[selectedEvent, users]` effektus `fetchAttendance` hívása maga lépteti (a
     * függvény ELSŐ sora `++attendanceSeqRef.current`), tehát az elavult válasz így is kiesik.
     * DE ez a védelem egy MÁS effektus mellékhatásán ül: ha valaha `setUsers([])` kerülne ebbe a
     * váltó effektusba (ami kézenfekvő takarítás lenne), a `users.length > 0` feltétel miatt a
     * `fetchAttendance` NEM futna le, a sorszám nem léptetődne, és a RÉGI esemény MÉG FUTÓ
     * jelenlét-válasza beírhatóvá válna az ÚJ esemény névsora fölé. Egy sor itt ezt a jövőbeli
     * lábon lövést kizárja.
     *
     * ⚠️ MÉRT KÖVETKEZMÉNY (2026-09-15, az S-3 tesztje közben derült ki): mivel a `users`-t itt
     * NEM ürítjük, közvetlenül a váltás után a `fetchAttendance` az ÚJ eseményre, de MÉG A RÉGI
     * névsorral fut le. Ebből KÉT dolog következett:
     *   (a) a RÉGI felhasználók kreditjét kérdezi újra, és ha az bukik, a kredit-figyelmeztető sáv
     *       a régi névsorra visszajön, amíg az új névsor meg nem érkezik (utána a saját
     *       kredit-állapota veszi át);
     *   (b) ⚠️ KORÁBBAN ITT AZ ÁLLT, hogy ez „nem jelöl semmit rosszul" — EZ HAMIS VOLT [S-3
     *       javítás, 2026-09-15]. A `fetchAttendance` a sorokat a `users`-ből építi, a táblát
     *       pedig CSAK a `loading` kapuzta: ha a jelenléti válasz előbb ért vissza, mint az új
     *       névsor, a RÉGI esemény névsora RENDERELŐDÖTT az ÚJ ív alatt, SZERKESZTHETŐEN
     *       (jelölők, összeg-mezők). Önjavuló volt (az új névsor-válasz felülírja), de egy gyors
     *       kattintás közben egy idegen esemény tagjára ment volna. Ezért van a `rosterPending`:
     *       az ív addig pörgőt mutat, amíg a KIVÁLASZTOTT esemény névsora le nem zárult.
     */
    attendanceSeqRef.current += 1;
    selectedEventRef.current = selectedEvent;
    // A névsor-kérés MOST indul; addig a régi névsor nem rajzolható ki az új ív alá [S-3].
    setRosterPending(!!selectedEvent);
    if (selectedEvent) {
      fetchEventParticipants(selectedEvent.eventId);
    }
  }, [selectedEvent]); // Remove fetchEventParticipants from dependency

  // Fetch attendance when participants are loaded
  useEffect(() => {
    if (selectedEvent && users.length > 0) {
      fetchAttendance(selectedEvent.eventId);
      fetchComments(selectedEvent.eventId);
      // Note: Makeup credits are fetched on-demand when user is marked present
      // This avoids unnecessary API calls and 404 errors for users without credits
    }
  }, [selectedEvent, users, canEdit]); // Remove fetchAttendance from dependency

  // Refresh attendance when refreshTrigger changes (e.g. after viewing user profile)
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0 && selectedEvent && users.length > 0) {
      fetchAttendance(selectedEvent.eventId);
    }
  }, [refreshTrigger]);

  const fetchComments = async (eventId: string) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/event-comments/event/${eventId}`);

      if (!response.ok) {
        console.error('Failed to fetch comments:', response.status, response.statusText);
        return;
      }

      const data = await response.json();
      console.log('Comments data received:', data);
      setComments(data);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
      setError('Nem sikerült betölteni a megjegyzéseket');
    }
  };

  /**
   * A PÓTLÁSI KREDITEK BETÖLTÉSE — a „nem tudjuk" LÁTSZIK, és nem 0 [B1, 2026-09-15].
   *
   * A háromféle kimenet (a részletes indoklás a `creditsFailed` állapotnál):
   *   • siker           → a szám bekerül a térképbe,
   *   • 404             → 0 kredit kerül be, CSENDBEN (a szerver válasza: nincs egyenleg-sor),
   *   • bármi MÁS hiba  → a felhasználó NEM kerül be a térképbe (tehát ISMERETLEN marad), a
   *                       `creditsFailed`-be viszont igen, és a szerver MAGYAR indoklása
   *                       (`apiErrorMessage`, axios-út) a maradó figyelmeztető sávba megy.
   *
   * ⚠️ A térképet ÖSSZEFÉSÜLJÜK, nem cseréljük: ez a függvény a teljes névsorra ÉS egyetlen
   * felhasználóra is hívódik (a jelen-pipa bekapcsolásakor), a csere pedig a többiek addig
   * megismert kreditjét törölte volna.
   */
  const fetchMakeupCredits = useCallback(async (userIds: string[]) => {
    if (!canEdit || userIds.length === 0) return;

    const known = new Map<string, number>();
    const failed: string[] = [];
    let failureMessage: string | null = null;

    await Promise.all(
      userIds.map(async (userId) => {
        try {
          const result = await trainingBalanceService.checkMakeupCredits(userId);
          known.set(userId, result.availableCredits);
        } catch (err: any) {
          const status = err?.response?.status ?? err?.status;
          if (status === 404) {
            // Nincs egyenleg-sora: ez ÉRDEMI válasz, nem hiba — 0 kredit, csendben.
            known.set(userId, 0);
            return;
          }
          console.error(`Failed to fetch makeup credits for user ${userId}:`, err);
          failed.push(userId);
          // A szerver saját magyar mondata, vagy a közös hálózati mondat (axios-hiba).
          failureMessage = failureMessage
            ?? requestErrorMessage(err, 'Nem sikerült lekérdezni a pótlási kreditet');
        }
      })
    );

    setMakeupCredits(prev => {
      const next = new Map(prev);
      known.forEach((value, userId) => next.set(userId, value));
      // Az ismeretlenné vált felhasználó KORÁBBI száma sem maradhat ott: azóta változhatott,
      // és a felületnek „?"-et kell mutatnia, nem egy elavult számot.
      failed.forEach(userId => next.delete(userId));
      return next;
    });

    // A ref az IGAZSÁG (nem a state): innen tudjuk SZINKRONBAN, maradt-e még ismeretlen
    // felhasználó, tehát eltűnhet-e a figyelmeztető sáv. A state csak a rajzoláshoz kell.
    const nextFailed = new Set(creditsFailedRef.current);
    known.forEach((_value, userId) => nextFailed.delete(userId));
    failed.forEach(userId => nextFailed.add(userId));
    creditsFailedRef.current = nextFailed;
    setCreditsFailed(nextFailed);

    if (failed.length > 0 && failureMessage) {
      const message: string = failureMessage;
      setCreditsError({
        message: `${message} A „Pótlás" jelölő addig zárva marad — készpénzt CSAK akkor kérj, ha a kredit tényleg nincs meg.`,
        retry: () => { void fetchMakeupCredits(userIds); },
      });
      showWarning('A pótlási kredit nem volt lekérdezhető.');
    } else if (nextFailed.size === 0) {
      // Már MINDEN érintett felhasználó kreditje ismert → a figyelmeztetés eltűnhet.
      setCreditsError(null);
    }
  }, [canEdit, showWarning]);

  /**
   * A pénz-mező DOM-elemének nyilvántartása (MUI `inputRef`). A `null` a kiszerelést jelenti —
   * olyankor törlünk, hogy egy eltávolított résztvevő elemére ne maradjon hivatkozás.
   */
  const registerPaymentInput = (userId: string) => (el: HTMLInputElement | null) => {
    if (el) {
      paymentInputsRef.current.set(userId, el);
    } else {
      paymentInputsRef.current.delete(userId);
    }
  };

  /**
   * A MENTÉST BLOKKOLÓ PÉNZ-HIBA BEJELENTÉSE — HÁROM úton, hogy ne lehessen néma [M2].
   * A `scrollIntoView` opcionális hívás: jsdom-ban nem létezik, és egy teszt-hiba miatt nem
   * bukhat el a mentés-őr (a `?.` NEM kozmetika).
   */
  const reportPaymentFieldError = (userId: string, message: string) => {
    setError(message);            // a lap tetején maradó sáv (aki felül van, ott látja)
    showError(message);           // toast (aki legörgetve dolgozik, ott látja)
    setPaymentFieldError({ userId, message }); // és MAGA A MEZŐ is megjelöli magát
    /**
     * SZŰRT NÉZETBEN REJTETT SOR [1. validátor-kör F5, 2026-10-04] — mérve (`c3b-val2/s11.spec.ts`):
     * a mentés jogosan megállt, a toast megnevezte a játékost, de a hibás mező a névszűrő miatt nem
     * volt a DOM-ban, így nem kapott fókuszt. Ilyenkor a szűrő ürül, és a fókusz/görgetés a
     * renderelés UTÁN fut (`pendingPaymentFocusRef` effektje).
     */
    if (!visibleAttendanceRecords.some(r => r.userId === userId)) {
      pendingPaymentFocusRef.current = userId;
      setNameFilter('');
      return;
    }
    const input = paymentInputsRef.current.get(userId);
    input?.scrollIntoView?.({ block: 'center' });
    input?.focus?.();
  };

  /**
   * ISMERETLEN-e a felhasználó pótlási kreditje [B1, 2026-09-15]. Külön függvény, hogy MIND A
   * HÁROM megjelenítő hely (mobil jelvény, asztali „Pótlási kredit" oszlop, „Pótlás" jelölő
   * buborék-szövege) UGYANABBÓL az egy forrásból döntsön — a lelet épp abból keletkezett, hogy
   * a „nincs kredit" és a „nem tudjuk" ugyanarra a 0-ra egyszerűsödött.
   */
  const isCreditUnknown = (userId: string) => creditsFailed.has(userId);

  /** A „Pótlás" jelölő buborék-szövege — ISMERETLEN állapotban KIMONDJA, hogy nem tudjuk. */
  const makeupTitle = (userId: string, isPresent: boolean) => {
    if (isCreditUnknown(userId)) {
      return 'A pótlási kredit nem volt lekérdezhető, ezért a pótlás nem jelölhető. Nem azt jelenti, hogy nincs kreditje — töltsd be újra.';
    }
    const credits = makeupCredits.get(userId) ?? 0;
    if (!isPresent || credits <= 0) {
      return 'Nincs elérhető pótlási kredit vagy a felhasználó nincs jelen';
    }
    return `${credits} pótlási kredit elérhető`;
  };

  const handleAttendanceChange = (userId: string, field: 'isPresent' | 'hasPaidOnSite' | 'isMakeup') => {
    setAttendanceRecords(prev =>
      prev.map(record => {
        if (record.userId === userId) {
          const updated = { ...record, [field]: !record[field] };

          // When marking a user as present, fetch their makeup credits on-demand
          if (field === 'isPresent' && updated.isPresent && canEdit) {
            // Only fetch if we don't already have the data
            if (!makeupCredits.has(userId)) {
              fetchMakeupCredits([userId]);
            }
          }

          // Reset makeup when unmarking presence
          if (field === 'isPresent' && !updated.isPresent) {
            updated.isMakeup = false;
            // Aki nincs jelen, annak részleges ablaka sincs (a szerver is törölné).
            updated.presentFrom = null;
            updated.presentUntil = null;
          }

          return updated;
        }
        return record;
      })
    );
  };

  // Az edző részleges jelenléti ablaka (Végig = null). Csak edző-soron hívódik.
  const handlePresenceWindowChange = (userId: string, window: PresenceWindow | null) => {
    setAttendanceRecords(prev =>
      prev.map(record =>
        record.userId === userId
          ? { ...record, presentFrom: window?.presentFrom ?? null, presentUntil: window?.presentUntil ?? null }
          : record
      )
    );
  };

  const presenceWindowOf = (record: AttendanceRecord): PresenceWindow | null =>
    record.presentFrom && record.presentUntil ? { presentFrom: record.presentFrom, presentUntil: record.presentUntil } : null;

  // Csak akkor mentünk, ha minden részleges ablak érvényes — a szerver amúgy is elutasítaná az egészet.
  const invalidPresenceWindow = selectedEvent
    ? attendanceRecords.some(r => {
        const w = presenceWindowOf(r);
        return !!w && !!validateWindow(selectedEvent.startTime, selectedEvent.endTime, w);
      })
    : false;

  const handleMarkAllPresent = () => {
    setAttendanceRecords(prev => {
      const updated = prev.map(record => ({ ...record, isPresent: true }));

      // Fetch makeup credits for all users when marking all present
      if (canEdit) {
        const userIds = updated.map(r => r.userId).filter(id => !makeupCredits.has(id));
        if (userIds.length > 0) {
          fetchMakeupCredits(userIds);
        }
      }

      return updated;
    });
  };

  /**
   * A „Fizetett/Beszedett összeg" mező bevitele → állapot.
   *
   * ⚠️ KONTROLLÁLT mező [NB-15, 2026-09-13]: a mező korábban `defaultValue` + `onBlur` volt, tehát a
   * beírt összeg a FÓKUSZ ELHAGYÁSÁIG csak a DOM-ban élt. Kattintós kilépésnél ez nem tűnt fel (a
   * kattintás blurt okoz), de a NEM-KATTINTÓS kilépés — F5, Ctrl+R, lapbezárás, böngésző-vissza —
   * mellett a `beforeunload` őr fel sem került, tehát a beírt összeg NÉMÁN elveszett (repró: egy
   * 2000-es sort 2800-ra írva F5 → kérdés nélkül, 2000-rel jött vissza). Onchange-re váltva a
   * gépelés AZONNAL a `attendanceRecords`-ba kerül, így a `hasUnsavedAttendance` is látja.
   *
   * ÜRES mező → `undefined`, NEM 0: a nulla forint érdemi érték („ingyen"), az üres mező pedig azt
   * jelenti, „nincs megadva" — a mobil app is így teszi. Ami nem tiszta számjegy-sorozat, az
   * ugyanígy „nincs megadva" (`parseForintInput`): NaN-t állapotba engedve a sor ÖRÖKRE piszkos
   * maradna (`NaN !== NaN`), és minden mentés újraküldené.
   *
   * ⚠️ A mező `type="text"` + `inputMode="numeric"`, NEM `type="number"` [NB-3, 2026-09-13]: a
   * szám-input a nem értelmezhető tartalomra ÜRES `value`-t ad (`validity.badInput`), amit a
   * kontrollált mező visszaírt a DOM-ba — így a „2800." beírásakor a mező a felhasználó szeme
   * előtt KIÜRÜLT. Szöveges mezővel a gépelt alak a komponensnél marad (`paymentDrafts`).
   *
   * ⚠️ A bevitel EGÉSZ FORINT, tizedes-jel NÉLKÜL [BL-2, 2026-09-14]: a `2.800` (magyarul
   * kétezer-nyolcszáz) korábban 2,8 Ft-ként ment ki a szerverre, NÉMÁN. A pont/vessző leütése ezért
   * VISSZA IS UTASÍTÁSRA kerül — a mező `2800`-at mutat, tehát a felhasználó LÁTJA, mi megy el.
   * A mérést és a döntés indoklását lásd a `utils/money.ts` fejlécében; a szabály minden pénz-mezőn
   * UGYANEZ (`isForintInputAccepted`).
   */
  const handlePaymentAmountChange = useCallback((userId: string, value: string, currentValue: string) => {
    // Csak számjegyet engedünk be: ami nem illeszkedik, azt EL SEM tároljuk, tehát a mező a
    // korábbi tartalmát mutatja tovább (a kontrollált mezőt a React állítja vissza).
    // A MOSTANI tartalmat is átadjuk, hogy egy RÉGI, tizedes összeg (ha a szerveren ilyen van)
    // takarítható legyen — a részleteket és a mérést lásd `isForintInputAccepted` [J-3].
    if (!isForintInputAccepted(value, currentValue)) return;
    // A mező-szintű hiba a JAVÍTÁS ELSŐ leütésére eltűnik [M2]: enélkül a piros keret és a súgó
    // akkor is ott maradt volna, amikor a felhasználó már kijavította az értéket.
    setPaymentFieldError(prev => (prev?.userId === userId ? null : prev));
    setPaymentDrafts(prev => {
      const next = new Map(prev);
      next.set(userId, value);
      return next;
    });
    const amount = parseForintInput(value);
    setAttendanceRecords(prev =>
      prev.map(record =>
        record.userId === userId
          ? { ...record, paymentAmount: amount }
          : record
      )
    );
  }, []);

  /** A mezőben MEGJELENÍTETT szöveg: a gépelt nyers alak, ha van, különben a mentett összeg. */
  const paymentFieldValue = (record: AttendanceRecord): string =>
    paymentDrafts.has(record.userId) ? paymentDrafts.get(record.userId)! : forintFieldValue(record.paymentAmount);

  /**
   * A MEGJEGYZÉS-al-dialógus mentetlen adata [NB-1, 2026-09-13].
   *
   * A beírt megjegyzés külön állapotban él (`newComment`), tehát nem része a jelenléti sorok
   * alapvonal-összevetésének — az al-dialógus bezárása (ESC, backdrop, „Mégse") eddig KÉRDÉS
   * NÉLKÜL eldobta a megírt szöveget. Ezt a záró utat ez a kapu zárja be.
   *
   * ⚠️ A NEM-KATTINTÓS kilépés (F5, Ctrl+R, lapbezárás) NEM ezen az úton megy: azt a
   * `beforeunload` fogja, ami csak bejelentett mentetlen adat mellett áll fel — ezért a beírt
   * megjegyzés a képernyő `isDirty`-jének is része (lásd `hasUnsavedCommentDraft`, BL-4).
   */
  const closeCommentDialog = async () => {
    if (newComment.trim() && !(await confirm(discardChangesConfirm('megjegyzés')))) return;
    setNewComment('');
    setCommentError(null);
    setCommentDialogOpen(false);
  };

  const handleSaveAttendance = async () => {
    if (!selectedEvent || !canEdit) return;

    setSaving(true);
    setError(null);

    try {
      // Csak a betöltéskori baseline-tól ELTÉRŐ (vagy újonnan hozzáadott) sorokat küldjük — így egy
      // másik edző párhuzamos mentése nem írja felül az általunk nem érintett sorokat (lost-update).
      const isChanged = (r: AttendanceRecord) => {
        const base = baselineRef.current.get(r.userId);
        if (!base) return true; // baseline-ban nincs → új sor (frissen hozzáadott résztvevő)
        // A mező-összevetés a KÖZÖS `isAttendanceRowDirty` (lásd ATTENDANCE_DIRTY_FIELDS): ha itt külön
        // lista élne, egy új mező kimaradhatna a mentésből, miközben a felület piszkosnak látja.
        return isAttendanceRowDirty(base, r);
      };
      const changedRecords = attendanceRecords.filter(r => isChanged(r) || addedParticipantIds.has(r.userId));

      if (changedRecords.length === 0) {
        // Nem siker, csak tájékoztatás — ne zöld siker-toaston jöjjön.
        showInfo('Nincs mentendő változás.');
        setSaving(false);
        return;
      }

      if (invalidPresenceWindow) {
        // [1. validátor-kör F3, 2026-10-04] a felső sáv mellett TOAST is: a Mentés gomb a rögzített
        // ALSÓ sávban van, a lap tetején lévő `Alert` legörgetett névsornál a látómezőn kívül esik.
        const presenceMessage = 'Az edző részleges jelenléte hibás — javítsd a kezdést/befejezést, aztán ments újra.';
        setError(presenceMessage);
        showError(presenceMessage);
        setSaving(false);
        return;
      }

      // A szerver felső korlátja (`AttendanceDto`: `[Range(0.0, 1_000_000.0, …)]`) KLIENS-oldalon
      // is [BL-2]: a BULK mentés bukása a TELJES névsor munkáját viszi magával, itt viszont
      // NÉVVEL meg tudjuk nevezni, kinek a sora a hibás, még mentés előtt. (A negatív összeg a
      // mező számjegy-szűrője miatt nem is előállítható — lásd `utils/money.ts`.)
      //
      // ⚠️ KOMMENT-JAVÍTÁS [2026-09-14, NB7 lelet]: itt korábban `[Range(0, 1000000)]` jelölés
      // állt (az ÉRTÉK ugyanaz, csak a jelölés elavult), és az az indoklás, hogy „a magyar
      // indoklás az `errors` mezőben utazik, amit a felület nem mutat meg (csak …(HTTP 400))".
      // Ez MA MÁR NEM IGAZ: a szerver 400-as válasza magyar `message`-t is ad, és a felület ki
      // is olvassa. Az őr azonban marad — az ADATVESZTÉS elleni érv (a teljes névsor) áll.
      /**
       * KÖZTES ÖSSZEG-VÁZLAT: a mentés MEGÁLL, a játékos NEVÉVEL [BL-2b, 2026-09-15].
       *
       * MI VOLT A BAJ (mérve): egy RÉGI, tizedes összeg takarítása (`"2.8"` → egy backspace →
       * `"2."`) elfogadott leütés (`isForintInputAccepted`, J-3), de a `parseForintInput` erre
       * `undefined`-ot ad, tehát a sor PISZKOS lett, a bulk kérésből a `paymentAmount` KIMARADT,
       * és a szerver a TÁROLT összeget NULL-ra írta
       * (`backend/PinyoApi/Controllers/AttendanceController.cs:412`
       * `existing.PaymentAmount = record.PaymentAmount`). A mező közben „2."-t mutatott — tehát a
       * felületen SZÁM látszott, a szerveren null lett: NÉMA adatvesztés.
       *
       * MIÉRT BLOKKOLÁS, és nem a helyszín-űrlap TARTALÉK-ÁGA (`parsed ?? legacy`): a vázlat itt
       * KIZÁRÓLAG felhasználói leütésre jön létre (`paymentDrafts` csak a
       * `handlePaymentAmountChange`-ben kap értéket), tehát az ÉRINTETLEN tizedes sor (nincs
       * vázlata) és a FÉLBEHAGYOTT takarítás megkülönböztethető — a tartalék viszont némán a régi
       * összeget mentené, miközben a mező mást mutat. A helyszínnél ez nem megy: ott a mező
       * állapota maga a betöltött nyers szöveg, ezért marad ott a tartalék (lásd `utils/money.ts`).
       *
       * A SZÁNDÉKOS TÖRLÉS ÉRINTETLEN: az üres mező (`""`) számjegy-alakú, tehát nem köztes —
       * `undefined` megy ki, és a szerver null-t ment („nincs megadva").
       */
      const unfinished = changedRecords.find(r => isUnfinishedForintDraft(paymentDrafts.get(r.userId)));
      if (unfinished) {
        // ⚠️ A blokkolás NEM lehet néma [M2]: toast + a MEZŐ megjelölése és fókuszálása is jár
        // hozzá, mert a felső sáv hosszú névsornál a látómezőn kívül van.
        reportPaymentFieldError(unfinished.userId, unfinishedForintMessage(unfinished.userName));
        setSaving(false);
        return;
      }

      const tooLarge = changedRecords.find(r => (r.paymentAmount ?? 0) > MAX_ATTENDANCE_PAYMENT);
      if (tooLarge) {
        reportPaymentFieldError(
          tooLarge.userId,
          `Túl nagy összeg: ${tooLarge.userName} — ${MAX_ATTENDANCE_PAYMENT.toLocaleString('hu-HU')} Ft-nál több nem adható meg.`
        );
        setSaving(false);
        return;
      }

      // Prepare bulk update — only the changed rows. Az edző időablaka is megy (üres = végig).
      const attendanceData = {
        eventId: selectedEvent.eventId,
        attendances: changedRecords.map(record => ({
          userId: record.userId,
          isPresent: record.isPresent,
          isMakeup: record.isMakeup,
          hasPaidOnSite: record.hasPaidOnSite,
          paymentAmount: record.paymentAmount,
          presentFrom: record.presentFrom ?? null,
          presentUntil: record.presentUntil ?? null,
        })),
      };

      /**
       * HÁLÓZATI HIBA MAGYARUL [1. validátor-kör F3 kísérője, 2026-10-04]: a `fetch` hálózati bukása
       * ANGOL `TypeError('Failed to fetch')`; amióta a mentési hiba toastban is megjelenik (F3), ez a
       * szöveg a látómező közepén ért volna a felhasználóhoz. A közös magyar mondatra cseréljük
       * (ugyanaz, mint a névsor/jelenlét betöltésénél, `fetchAttendance`). CSAK a `fetch` hívást
       * fogja: egy későbbi programhiba (szintén `TypeError`) nem álcázódhat hálózati hibának.
       */
      let response: Response;
      try {
        response = await authFetch(`${API_BASE_URL}/api/attendance/bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(attendanceData),
        });
      } catch (networkErr) {
        console.error('Failed to save attendance (network):', networkErr);
        throw new Error(NETWORK_ERROR_MESSAGE);
      }
      // [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03] A MENTÉS 401-e: a közös jelző nyitja az
      // újra-belépő ablakot (a mentetlen sorok miatt NEM jelentkeztet ki), a pipák és a `dirtyRows`
      // a memóriában maradnak (ez az ág csak hibát állít, állapotot nem töröl). A régi szöveg a nyers
      // „Nem sikerült menteni a jelenlétet (HTTP 401)" volt (a régi `:1396-1400`).
      // [Web 7 / T1, 2026-10-05] A jelzést ma az `authFetch` adja (a válasz visszaadása ELŐTT), és
      // előtte egy csendes megújítással egyszer meg is ismétli a mentést — a 401 csak akkor ér ide,
      // ha a megújítás sem sikerült.

      if (!response.ok) {
        // A szerver JSON-t ad (`{"message":"…"}`), és eddig ez a NYERS JSON jelent meg a
        // felületen (pl. az idegen esemény 403-ánál). Innentől a magyar mondat látszik.
        // [3. javító kör, F-1] a lejárat-mondatot a közös, zár-tudatos `saveResponseErrorMessage` adja
        // (az `authFetch` a `reportFetchUnauthorized`-dal már beállította a zárat a saját tokenünk 401-ére).
        const errorText = await response.text().catch(() => '');
        throw new Error(saveResponseErrorMessage(errorText, response.status, 'Nem sikerült menteni a jelenlétet'));
      }

      showSuccess('Jelenlét mentve.');

      // Refresh attendance data (ez frissíti a baseline-t is); a frissen hozzáadott résztvevők
      // mostantól perzisztáltak, így kikerülnek a "mindig küldendő" halmazból.
      setAddedParticipantIds(new Set());
      // A kiküldött sorok baseline-ja a MENTETT értékre lép, MIELŐTT újratöltünk: az összefésülő betöltő
      // (A3) csak a baseline-tól eltérő sorokat tartja meg helyben — enélkül a most mentett sor örökre
      // „piszkos" maradna, minden további mentés újraküldené (a társ-edző módosítását felülírva), és a
      // „Nincs mentendő változás" sem jönne elő (validátor-blokkoló).
      changedRecords.forEach(r => baselineRef.current.set(r.userId, {
        isPresent: r.isPresent, hasPaidOnSite: r.hasPaidOnSite, isMakeup: r.isMakeup, paymentAmount: r.paymentAmount,
        presentFrom: r.presentFrom ?? null, presentUntil: r.presentUntil ?? null,
      }));
      /**
       * A GÉPELT NYERS SZÖVEGEK IS ELFOGYNAK A MENTÉSSEL [S3, 2026-09-15].
       *
       * MIÉRT: a `paymentDrafts` a felhasználó nyers leütéseit tartja (pl. `"08000"`), és a
       * mező ELSŐBBSÉGGEL ezt mutatja (`paymentFieldValue`). Mentés után a szerver értéke az
       * igazság, tehát a vázlatnak el kell tűnnie — enélkül a mező a mentés UTÁN is a gépelt
       * alakot mutatta (`"08000"` a szerver 8000-e helyett), és a köztes-vázlat-őr (BL-2b) egy
       * félbehagyott, DE MÁR MENTETT sorra is blokkolhatta a következő mentést.
       * A baseline-léptetéssel EGYÜTT kell lennie: a betöltő a vázlatokat a piszkos sorok
       * szerint tartja meg, a most mentett sorok pedig már nem piszkosak.
       */
      setPaymentDrafts(new Map());
      setPaymentFieldError(null);
      await fetchAttendance(selectedEvent.eventId);
    } catch (err: any) {
      const message = err.message || 'Nem sikerült menteni a jelenlétet';
      setError(message);
      /**
       * TOAST IS [1. validátor-kör F3, 2026-10-04]: a Mentés a rögzített ALSÓ sávban van, a felső
       * `Alert` legörgetve nem látszik — a szerver magyar indoklása (`saveResponseErrorMessage`)
       * így a felhasználó látómezejében is megjelenik.
       * KIVÉTEL: LEJÁRT munkamenet (`isSessionExpiredLocked`). Ilyenkor a mondatot az újra-belépő
       * ablak (modális, látható) vagy a belépő oldal mondja ki; a `showError` saját elnyelése
       * (`shouldSuppressSessionToast`) NYITOTT ablaknál szándékosan NEM nyel — ott a toast az ablak
       * mögött duplázná a „… a változásaid megmaradtak …" mondatot. A lejárat-mondat konstansával
       * nem hasonlíthatunk: azt ebben a fájlban a forrás-őr (`unsavedCoverage.test.ts`
       * „lejárat-tudatos burkolón át megy") tiltja. Őr: `EventAttendance.sessionExpired.test.tsx`
       * (401 → a mondat EGYSZER látszik, toast nincs).
       */
      if (!isSessionExpiredLocked()) showError(message);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedEvent || !newComment.trim()) return;

    setCommentError(null);
    try {
      console.log('Creating comment for event:', selectedEvent.eventId, 'Comment:', newComment);

      // [Web UX 3. csomag (D), 2026-10-04] HÁLÓZATI HIBA MAGYARUL — ugyanaz a minta, mint a jelenlét mentésénél: a `fetch`
      // hálózati bukása ANGOL `TypeError('Failed to fetch')`, és a catch az `err.message`-et írta ki.
      // CSAK a `fetch` hívást fogjuk: egy későbbi programhiba nem álcázódhat hálózati hibának.
      let response: Response;
      try {
        response = await authFetch(`${API_BASE_URL}/api/event-comments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            eventId: selectedEvent.eventId,
            comment: newComment,
          }),
        });
      } catch (networkErr) {
        console.error('Failed to add comment (network):', networkErr);
        throw new Error(NETWORK_ERROR_MESSAGE);
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to add comment:', response.status, response.statusText, errorText);
        throw new Error(saveResponseErrorMessage(errorText, response.status, 'Nem sikerült hozzáadni a megjegyzést'));
      }

      const newCommentData = await response.json();
      console.log('Comment created successfully:', newCommentData);

      setNewComment('');
      setCommentDialogOpen(false);
      await fetchComments(selectedEvent.eventId);
      showSuccess('Megjegyzés hozzáadva.');
    } catch (err: any) {
      const message = err.message || 'Nem sikerült hozzáadni a megjegyzést';
      setError(message);
      // [7. javító kör — validátor F3] a nyitva maradó dialógusban is (a lap-sáv mögötte van)
      setCommentError(message);
      console.error('Error adding comment:', err);
    }
  };

  /**
   * MIÉRT nem szerkeszthető az ív — `null`, ha szerkeszthető [BL-4, 2026-09-15].
   *
   * A `participantsLoaded` azért megy át, hogy a betöltetlen/elbukott névsor NE néma „nincs
   * jogod"-ként jelenjen meg: a `'participantsUnknown'` ágat a felület KIÍRJA, „Újra" gombbal.
   */
  const lockReason = (): AttendanceLockReason => {
    if (!selectedEvent) return 'notAllowed';

    return attendanceLockReason({
      isAdmin,
      isCoach,
      isPaidOut: !!selectedEvent.isPaidOut,
      isEventCreator: !!selectedEvent.createdBy && !!user?.userId
        && selectedEvent.createdBy.toLowerCase() === user.userId.toLowerCase(),
      isAssignedCoach: users.some(u =>
        u.userId?.toLowerCase() === user?.userId?.toLowerCase()
        && isCoachRole(u.userRoleSystemName, u.userRoleName)),
      participantsLoaded,
    });
  };

  const canEditEvent = () => lockReason() === null;

  // Fetch available users and roles for participant management
  const fetchAvailableUsersAndRoles = useCallback(async () => {
    try {
      const [usersResult, rolesResult] = await Promise.all([
        userService.getUsers({ isActive: true }),
        userService.getUserRoles()
      ]);

      // Filter out users who are already participants
      const participantUserIds = new Set(users.map(u => u.userId));
      const filteredUsers = usersResult.data.filter(u => !participantUserIds.has(u.userId));

      setAvailableUsers(filteredUsers);
      setAvailableRoles(rolesResult.filter(r => r.isActive && r.name !== 'Administrator'));
      setAddParticipantError(null);
    } catch (err) {
      console.error('Failed to fetch users and roles:', err);
      // UGYANAZ A NÉMA OSZTÁLY, mint a résztvevő-lekérésnél [BL-4, 2026-09-15]: a hiba eddig csak
      // a konzolra ment, a dialógus pedig „Nincs elérhető felhasználó"-t mutatott — a felhasználó
      // azt hitte, nincs kit felvenni, holott a lekérés bukott el. Itt `apiErrorMessage` kell
      // (axios-út, a `userService`-en keresztül), nem `responseErrorMessage`.
      setAddParticipantError(requestErrorMessage(err, 'Nem sikerült betölteni a felhasználókat'));
    }
  }, [users]);

  const handleOpenAddParticipantDialog = () => {
    setAddParticipantDialogOpen(true);
    setSelectedUserToAdd(null);
    setSelectedRoleForNewParticipant('');
    setAddParticipantError(null);
    setAddSaveError(null);
    fetchAvailableUsersAndRoles();
  };

  const handleCloseAddParticipantDialog = () => {
    setAddParticipantDialogOpen(false);
    setSelectedUserToAdd(null);
    setSelectedRoleForNewParticipant('');
    setAddParticipantError(null);
    setAddSaveError(null);
  };

  const handleAddParticipant = async () => {
    if (!selectedEvent || !selectedUserToAdd || !selectedRoleForNewParticipant) {
      setError('Kérem válasszon felhasználót és szerepkört');
      return;
    }

    try {
      setAddingParticipant(true);
      setError(null);
      setAddSaveError(null);

      await calendarService.addParticipantToEvent(
        selectedEvent.eventId,
        selectedUserToAdd.userId,
        selectedRoleForNewParticipant
      );

      // Track this as an added participant
      setAddedParticipantIds(prev => new Set(prev).add(selectedUserToAdd.userId));
      // [1. validátor-kör, megjegyzés, 2026-10-04] Szűrt nézetben a frissen felvett résztvevő ne
      // tűnjön el: ha a neve nem illik a keresőszóra, a szűrő ürül (különben a felvétel „nem
      // történt meg"-nek látszana).
      if (!matchesNameFilter(selectedUserToAdd.name, nameFilter)) setNameFilter('');

      // Reload participants and attendance
      await fetchEventParticipants(selectedEvent.eventId);

      showSuccess('Résztvevő hozzáadva.');
      handleCloseAddParticipantDialog();
    } catch (err: any) {
      const message = requestErrorMessage(err, 'Nem sikerült hozzáadni a résztvevőt');
      setError(message);
      // [7. javító kör — validátor F3] a nyitva maradó dialógusban is (a lap-sáv mögötte van)
      setAddSaveError(message);
      console.error(err);
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleRemoveParticipant = async (userId: string) => {
    if (!selectedEvent) return;

    const userToRemove = users.find(u => u.userId === userId);
    if (!userToRemove) return;

    const confirmed = await confirm({
      title: 'Résztvevő eltávolítása',
      message: `Biztosan eltávolítja ${userToRemove.name} résztvevőt az eseményről?`,
      confirmLabel: 'Eltávolítás',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      setError(null);

      await calendarService.removeParticipantFromEvent(selectedEvent.eventId, userId);

      // Remove from added participants set
      setAddedParticipantIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });

      // Reload participants and attendance
      await fetchEventParticipants(selectedEvent.eventId);

      showSuccess('Résztvevő eltávolítva.');
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Nem sikerült eltávolítani a résztvevőt'));
      console.error(err);
    }
  };

  /**
   * „Név keresése" mező [Web UX 3. csomag (B), 2026-10-04] — 10 fő FELETT, mindkét elrendezésben.
   * Szűrt állapotban a segédszöveg KIMONDJA, hogy az „Összes jelen" és a Mentés a rejtett sorokra
   * is vonatkozik (a validátor kérdése: mit csinál az „Összes jelen" szűrt nézetben?).
   */
  const renderNameFilter = () => {
    if (!showNameFilter) return null;
    return (
      <TextField
        label="Név keresése"
        size="small"
        value={nameFilter}
        onChange={(e) => setNameFilter(e.target.value)}
        fullWidth
        inputProps={{ 'data-testid': 'attendance-name-filter' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        FormHelperTextProps={{ 'data-testid': 'attendance-name-filter-help' } as any}
        helperText={isNameFiltered
          ? `${visibleAttendanceRecords.length} / ${attendanceRecords.length} résztvevő látszik. Az „Összes jelen" és a Mentés a szűrő által elrejtett résztvevőkre is vonatkozik.`
          : undefined}
      />
    );
  };

  /** Üres találat a szűrőre — hogy a „nincs sor" ne tűnjön üres névsornak. */
  const renderNoFilterMatch = () => (
    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }} data-testid="attendance-name-filter-empty">
      Nincs a keresésnek megfelelő résztvevő.
    </Typography>
  );

  // Mobile-specific components
  // RENDER-FÜGGVÉNY, nem beágyazott komponens [Web UX A4]: a render-en belül definiált komponens minden
  // állapotváltásnál új típus → React unmount/remount, a még nem committolt (onBlur-ra váró) fizetett-összeg
  // mezők tartalma elveszhetett. Függvényhívásként a fa stabil marad.
  const renderMobileEventCard = (event: Event) => {
    const eventPresentCount = attendanceRecords.filter(record => record.isPresent).length;
    const eventTotalParticipants = attendanceRecords.length;
    const eventAttendancePercentage = eventTotalParticipants > 0 ? Math.round((eventPresentCount / eventTotalParticipants) * 100) : 0;

    return (
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={event.eventName}
          subheader={format(new Date(event.eventDate), 'yyyy. MMMM dd.', { locale: hu })}
          action={
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              {event.isPaidOut && (
                <Chip
                  label="Kifizetett – nem módosítható"
                  color="success"
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              <Chip
                label={`${eventPresentCount}/${eventTotalParticipants}`}
                color="primary"
                size="small"
              />
              <Chip
                label={`${eventAttendancePercentage}%`}
                color={eventAttendancePercentage >= 70 ? 'success' : 'warning'}
                size="small"
              />
              {event.maxParticipants && (
                <Chip
                  label={`${eventTotalParticipants}/${event.maxParticipants} kapacitás`}
                  color={
                    eventTotalParticipants >= event.maxParticipants
                      ? 'error'
                      : eventTotalParticipants >= event.maxParticipants * 0.8
                        ? 'warning'
                        : 'success'
                  }
                  size="small"
                />
              )}
            </Box>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {event.venueName && (
              <Chip label={event.venueName} size="small" />
            )}
            {event.eventTypeName && (
              <Chip label={event.eventTypeDisplayName || event.eventTypeName} color="secondary" size="small" />
            )}
          </Box>
          
          <Accordion
            expanded={accordionExpanded}
            onChange={(event, isExpanded) => setAccordionExpanded(isExpanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 48,
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0',
                  alignItems: 'center'
                }
              }}
            >
              <Typography variant="subtitle1">
                Résztvevők kezelése ({eventTotalParticipants}
                {event.maxParticipants ? `/${event.maxParticipants}` : ''} fő)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {/* `rosterPending` is [S-3]: a RÉGI névsor nem rajzolható ki az ÚJ esemény ÍVE alá. */}
              {loading || rosterPending ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Rendezett + szűrt NÉZET [Web UX 3. csomag (B), 2026-10-04] — az állapot sorrendje nem változik. */}
                  {renderNameFilter()}
                  {isNameFiltered && visibleAttendanceRecords.length === 0 && renderNoFilterMatch()}
                  {visibleAttendanceRecords.map((record) => (
                    <Paper
                      key={record.userId}
                      /*
                        KÖZÖS SOR-GYÖKÉR TESTID [B-M8, 2026-09-16].
                        A jelenléti ív 900 px alatt KÁRTYÁS elrendezésre vált (`isSmallScreen`,
                        `:2562`), ahol NINCS `<tr>` — a specek `getByRole('row')`-ja tehát
                        telefon-nézetben NULLA sort talált. Ugyanez a testid van az asztali
                        `<TableRow>`-n is, hogy EGY lokátor mindkét elrendezésben a sor gyökerére
                        címezzen (`tests/e2e/helpers/attendance.ts`).
                      */
                      data-testid="attendance-row"
                      variant="outlined"
                      sx={{
                        p: { xs: 1, sm: 2 },
                        borderRadius: 2,
                        /*
                          [Web 7 / T2, 2026-10-06] A jelenlévő kártya háttere a `success.light`
                          helyett halvány, a papírra kevert zöld (`attendanceCardBg`). MÉRVE: a
                          sötét témában a `success.light` (#7bc67e) a világos szöveggel 1,83:1
                          volt — a kártya teljes szövege olvashatatlan; világosban a narancs
                          „Fizetett" címke a zöldön 1,36:1. Most ≥ 4,5:1 mindkét témában
                          (őr: `theme/__tests__/readableColors.contrast.test.ts`).
                        */
                        backgroundColor: (t: Theme) => attendanceCardBg(t, record.isPresent),
                        opacity: record.isPresent ? 1 : 0.7,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          {/*
                            A NÉV A KÁRTYÁN IS MEGNYITJA AZ ADATLAPOT [B-W5, 2026-09-16].

                            ══ MI VOLT A BAJ (MÉRVE: Playwright „Mobile Chrome", Pixel 5, 393 px) ══
                            900 px alatt a jelenléti ív KÁRTYÁS elrendezésre vált
                            (`isSmallScreen`, `:2499`), és a kártyán a név sima `Typography` volt —
                            KATTINTHATATLAN. Az adatlap-ablak (`UserProfileDialog`) tehát a
                            telefonról/tabletről EGYÁLTALÁN nem volt megnyitható a jelenléti ívről,
                            pedig a felület MINDEN ide vezető útja ez az egy név:
                              · SOS — VÉSZHELYZETI adathozzáférés (szülő telefonszáma, lakcím). Ez
                                az a funkció, amire épp a helyszínen, TELEFONRÓL van szükség;
                              · a tagra írt megjegyzések (a kártyán ott van az olvasatlan-jelvény,
                                de nem volt, ahonnan megnyitható);
                              · a magánóra-díj / adatlap megtekintése.
                            Az asztali táblázat ugyanezt a nevet EDDIG IS gombként adta
                            (`:2088-2105`), tehát a két elrendezés funkciója szétcsúszott — a
                            kártyás ág egy egész képernyőt zárt el.

                            A javítás SZÁNDÉKOSAN ugyanazt a `Link component="button"` mintát
                            használja, mint az asztali ág, hogy a két elrendezés ne csúszhasson
                            újra szét (és hogy a teszt-keresés `getByRole('button', { name })`-je
                            mindkettőn működjön).
                          */}
                          {onNavigateToUserProfile ? (
                            <Link
                              component="button"
                              variant="subtitle2"
                              onClick={() => onNavigateToUserProfile(record.userId)}
                              sx={{
                                fontWeight: 600,
                                textAlign: 'left',
                                cursor: 'pointer',
                                textDecoration: 'none',
                                // [Web 7 / T2, 2026-10-06] A név-link `primary.main` színe a böngészőben MÉRVE (E2E
                                // `flow-readable-highlights`): a jelenlévő kártyán világosban 3,91:1, sötétben 2,09:1
                                // (a sötét téma #00695C-je sötét háttéren). Az `attendanceToneText` minden háttéren ≥ 4,5:1.
                                color: (t: Theme) => attendanceToneText(t, 'primary'),
                                // Telefonon a koppintási felület legalább 44 px magas legyen.
                                minHeight: 24,
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {record.userName}
                            </Link>
                          ) : (
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {record.userName}
                            </Typography>
                          )}
                          {/* ISMERETLEN kredit: a kártyán is KIÍRJUK, hogy nem tudjuk — némán
                              „nincs kredit"-ként megjelenni tilos [B1, 2026-09-15]. */}
                          {isCreditUnknown(record.userId) ? (
                            <Chip
                              label="pótlás: ?"
                              size="small"
                              color="warning"
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                              data-testid={`makeup-credit-unknown-${record.userId}`}
                              title="A pótlási kredit nem volt lekérdezhető — ez NEM azt jelenti, hogy nincs kreditje."
                            />
                          ) : (makeupCredits.get(record.userId) || 0) > 0 && (
                            <Chip
                              label={`${makeupCredits.get(record.userId)} pótlás`}
                              size="small"
                              color="info"
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {record.isPresent && <CheckCircleIcon color="success" fontSize="small" />}
                          {record.hasPaidOnSite && <MoneyIcon color="success" fontSize="small" />}
                          {canManageParticipants && addedParticipantIds.has(record.userId) && (
                            <Tooltip title="Résztvevő eltávolítása">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveParticipant(record.userId)}
                                sx={{ ml: 1 }}
                                aria-label={`Résztvevő eltávolítása: ${record.userName}`}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      
                      <Box sx={{
                        display: 'flex',
                        gap: { xs: 1, sm: 2 },
                        mt: 2,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        pt: 1.5
                      }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={record.isPresent}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleAttendanceChange(record.userId, 'isPresent');
                              }}
                              disabled={!canEditEvent()}
                              color="success"
                              sx={{
                                p: { xs: 0.5, sm: 1.5 },
                                '& .MuiSvgIcon-root': { fontSize: { xs: 20, sm: 28 } }
                              }}
                            />
                          }
                          label="Jelen"
                          sx={{
                            flex: { xs: '0 0 auto', sm: 1 },
                            margin: 0,
                            minWidth: 0,
                            px: { xs: 0.5, sm: 0 },
                            py: { xs: 0.5, sm: 0 },
                            borderRadius: 1,
                            // [Web 7 / T2, 2026-10-06] WEB_UX_2KOR (`Documents/WEB_UX_JAVASLATOK_2KOR_2026-10-04.md`) 4.1: a korábbi `'success.lighter'` palettakulcs
                            // a MUI 7.3.2-ben NEM létezik → érvénytelen CSS, a kiemelés soha nem látszott.
                            // A szín a közös, kontraszt-őrrel mért receptből jön (`theme/readableColors.ts`).
                            backgroundColor: record.isPresent ? (t: Theme) => attendanceMarkBg(t, 'success') : 'transparent',
                            '& .MuiFormControlLabel-label': {
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              fontWeight: record.isPresent ? 600 : 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={record.isMakeup}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleAttendanceChange(record.userId, 'isMakeup');
                              }}
                              // ISMERETLEN kredit → a jelölő ZÁRVA marad: vaktában nem adunk
                              // kreditet [B1]. (Ismeretlennél nincs bejegyzés, tehát a `|| 0`
                              // magától 0-t ad — a KÜLÖNBSÉG a jelvényben és a buborékban látszik.)
                              disabled={!canEditEvent() || !record.isPresent || (makeupCredits.get(record.userId) || 0) <= 0}
                              color="info"
                              title={makeupTitle(record.userId, record.isPresent)}
                              sx={{
                                p: { xs: 0.5, sm: 1.5 },
                                '& .MuiSvgIcon-root': { fontSize: { xs: 20, sm: 28 } }
                              }}
                            />
                          }
                          label="Pótlás"
                          sx={{
                            flex: { xs: '0 0 auto', sm: 1 },
                            margin: 0,
                            minWidth: 0,
                            px: { xs: 0.5, sm: 0 },
                            py: { xs: 0.5, sm: 0 },
                            borderRadius: 1,
                            // [Web 7 / T2, 2026-10-06] WEB_UX_2KOR 4.1: a korábbi `'info.lighter'` palettakulcs
                            // a MUI 7.3.2-ben NEM létezik → érvénytelen CSS, a kiemelés soha nem látszott.
                            // A szín a közös, kontraszt-őrrel mért receptből jön (`theme/readableColors.ts`).
                            backgroundColor: record.isMakeup ? (t: Theme) => attendanceMarkBg(t, 'info') : 'transparent',
                            '& .MuiFormControlLabel-label': {
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              fontWeight: record.isMakeup ? 600 : 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }
                          }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, gap: 1, flex: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={record.hasPaidOnSite}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleAttendanceChange(record.userId, 'hasPaidOnSite');
                                }}
                                disabled={!canEditEvent()}
                                color="success"
                                sx={{
                                  p: { xs: 0.5, sm: 1.5 },
                                  '& .MuiSvgIcon-root': { fontSize: { xs: 20, sm: 28 } }
                                }}
                              />
                            }
                            label={
                              /* [Web 7 / T2] `warning.dark` világosban fehéren 3,79:1, a régi zöld kártyán 1,36:1 volt;
                                 az `attendanceToneText` a kártya MINDEN hátterén ≥ 4,5:1. */
                              <Box component="span" sx={{ color: (t: Theme) => attendanceToneText(t, isCoachRow(record.userId) ? 'success' : 'warning') }}>
                                {isCoachRow(record.userId) ? 'Beszedett' : 'Fizetett'}
                              </Box>
                            }
                            sx={{
                              margin: 0,
                              minWidth: 0,
                              flex: { xs: '0 0 auto', sm: 'initial' },
                              px: { xs: 0.5, sm: 0 },
                              py: { xs: 0.5, sm: 0 },
                              borderRadius: 1,
                              // [Web 7 / T2, 2026-10-06] WEB_UX_2KOR 4.1: a korábbi `'warning.lighter'` palettakulcs
                              // a MUI 7.3.2-ben NEM létezik → érvénytelen CSS, a kiemelés soha nem látszott.
                              // A szín a közös, kontraszt-őrrel mért receptből jön (`theme/readableColors.ts`).
                              backgroundColor: record.hasPaidOnSite ? (t: Theme) => attendanceMarkBg(t, 'warning') : 'transparent',
                              '& .MuiFormControlLabel-label': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                fontWeight: record.hasPaidOnSite ? 600 : 500,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }
                            }}
                          />
                          {record.privateLessonFee != null && (
                            /* [Web 7 / T2] `primary.main` sötét témában (#00695C) a sötét kártyán olvashatatlan volt. */
                            <Typography variant="caption" data-testid="attendance-fee-due" sx={{ color: (t: Theme) => attendanceToneText(t, 'primary'), fontWeight: 700, whiteSpace: 'nowrap' }}>
                              Beszedendő: {record.privateLessonFee.toLocaleString('hu-HU')} Ft
                            </Typography>
                          )}
                          {record.hasPaidOnSite && (
                            <Box>
                              <TextField
                                key={record.userId}
                                size="small"
                                type="text"
                                label="Összeg (Ft)"
                                inputProps={{
                                  'data-testid': `attendance-amount-${record.userId}`,
                                  inputMode: 'numeric',
                                }}
                                // A mentés-őr IDE fókuszál és IDE görget [M2, 2026-09-15].
                                inputRef={registerPaymentInput(record.userId)}
                                error={paymentFieldError?.userId === record.userId}
                                helperText={paymentFieldError?.userId === record.userId ? paymentFieldError.message : undefined}
                                value={paymentFieldValue(record)}
                                onChange={(e) => handlePaymentAmountChange(record.userId, e.target.value, paymentFieldValue(record))}
                                disabled={!canEditEvent()}
                                sx={{ width: { xs: '90px', sm: '100px' } }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Edző sora telefonon: a pipasor ALÁ kerül a Végig / Részben blokk */}
                      {isCoachRow(record.userId) && record.isPresent && (
                        <Box sx={{ mt: 1.5 }}>
                          <CoachPresenceControl
                            eventStart={event.startTime}
                            eventEnd={event.endTime}
                            value={presenceWindowOf(record)}
                            onChange={w => handlePresenceWindowChange(record.userId, w)}
                            disabled={!canEditEvent()}
                            compact
                            testIdPrefix={`coach-presence-${record.userId}`}
                          />
                        </Box>
                      )}

                      {record.recordedByName && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary">
                            Rögzítette: {record.recordedByName}
                            {/* [Web UX 1. csomag (4.3), 2026-10-03] a `recordedDate` UTC, „Z” nélkül jön → közös segéd (budapesti idő). */}
                            {record.recordedDate && (
                              ` • ${formatServerDateTime(record.recordedDate, { withoutYear: true })}`
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
          
          {/* Action buttons for mobile */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexDirection: 'column' }}>
            <Button
              variant="outlined"
              startIcon={<CommentIcon />}
              onClick={() => setCommentDialogOpen(true)}
              size="small"
              fullWidth
              sx={{ minHeight: 44 }}
            >
              MEGJEGYZÉSEK ({comments.length})
            </Button>
            {canManageParticipants && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PersonAddIcon />}
                onClick={handleOpenAddParticipantDialog}
                size="small"
                fullWidth
                sx={{ minHeight: 44 }}
              >
                RÉSZTVEVŐ HOZZÁADÁS
              </Button>
            )}
            {canEditEvent() && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<DoneAllIcon />}
                  onClick={handleMarkAllPresent}
                  disabled={saving}
                  size="small"
                  fullWidth
                  sx={{ minHeight: 44 }}
                >
                  ÖSSZES JELEN
                </Button>
                {/* A Mentés gomb INNEN (a kártya végéről) a képernyő alján rögzített mentés-sávba
                    KÖLTÖZÖTT, számlálóval [Web UX 9 / T3, 2026-10-13] — lásd `renderSaveBar`. A
                    `data-testid="attendance-save"` vele ment: a kártyás ágon is EGY van. A felirat
                    („Mentés", nem „JELENLÉT MENTÉS") egyesítésének indoka [B-M8 → Web UX 3. csomag (B)]:
                    a mobil app is „Mentés"-t ír, és 14 E2E-hely `/^Mentés/`-sel keres (köztük
                    `toHaveCount(0)` negatív állítások) — egy harmadik felirat ezeket hamis zölddé tenné. */}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  /**
   * RÖGZÍTETT MENTÉS-SÁV [Web UX 3. csomag (B), 2026-10-04 — jelentés 1.3; Web UX 9 / T3, 2026-10-13].
   *
   * MI VOLT A BAJ: a Mentés a táblázat FEJLÉCÉBEN állt; egy 15+ fős névsor aljáról vissza
   * kellett görgetni, és közben semmi nem mutatta, hány sor vár mentésre.
   *
   * A sáv a képernyő ALJÁN marad („N mentetlen változás · Mentés"). Az N a
   * `computeDirtyUserIds(...)` — UGYANAZ a forrás, mint a kilépési kérdésé (`isDirty`).
   * Kifizetett / nem szerkeszthető eseményen (`canEditEvent()` hamis) NINCS sáv és gomb — a
   * `toHaveCount(0)` őrök (`flow-payments-ui`, `flow-quick-wins-extra`,
   * `flow-attendance-foreign-coach`) ezt mérik.
   *
   * [Web UX 9 / T3] A KÁRTYÁS (<900 px) ELRENDEZÉSBEN IS EZ A SÁV ÉL. Eddig csak ≥900 px-en létezett
   * (lelet: `EventAttendance.tsx:2722-2790` a 2253e81-en), a kártyás ág Mentés gombja a lista VÉGÉN
   * állt (`:2359-2384`), számláló nélkül — egy 20 fős csoportnál görgetés után. Most EGY forrás, EGY
   * kezelő, EGY letiltási logika, és mindkét elrendezésben pontosan EGY `attendance-save` van (a
   * kártya végéről a gomb kikerült; a `tests/e2e/helpers/attendance.ts` `saveButton` változatlan).
   *
   * ELHELYEZÉS — MÉRVE Playwright-tal (`flow-attendance-v2.spec.ts` „kártyás nézet” esetei, 390 / 360 / 768 px):
   *  · ≥600 px: `position: fixed`, a képernyő alján. NEM `sticky`: az `index.css` a `html, body`-ra
   *    `overflow-x: hidden`-t tesz, amitől a `body` görgető-őssé válik, és a `sticky` ott nem a
   *    nézetablakhoz tapad. Alatta a `attendance-save-bar-spacer` tartja szabadon a tartalom alját
   *    (az utolsó kártya / sor nem csúszik a sáv alá).
   *  · <600 px: `position: sticky; bottom: 0`. Itt a `Dashboard` a lapot egy SAJÁT görgető dobozba
   *    teszi (`Dashboard.tsx` `height: 100dvh` + `overflowY: 'auto'`), az alsó menü (`MobileBottomNav`)
   *    pedig e doboz ALATT, a folyamban ül (nem `fixed`). A `sticky` ennek a doboznak az aljához
   *    tapad, vagyis PONTOSAN a menü fölé — bűvös szám (menü-magasság) nélkül, safe-area-val együtt.
   *    Folyamban van, ezért spacer sem kell: a lista végén a sáv a lista UTÁN áll.
   *    A `fixed` itt a menüre esne (a menü a nézet alján van), ezért nem az.
   * A jelenléti ív NEM dialógusban, hanem a Dashboard fülén jelenik meg (`Dashboard.tsx`
   * `attendanceTabIndex`), így nincs transzformált ős, ami a `fixed`-et elvinné.
   */
  const renderSaveBar = () => {
    if (!canEditEvent()) return null;
    const stickyInScroller = isMobile;
    return (
      <>
        {!stickyInScroller && <Box sx={{ height: 72 }} data-testid="attendance-save-bar-spacer" />}
        <Paper
          ref={setSaveBarRef}
          elevation={SAVE_BAR_ELEVATION}
          square
          data-testid="attendance-save-bar"
          role="region"
          aria-label="Jelenléti ív mentése"
          sx={{
            ...(stickyInScroller
              ? {
                  position: 'sticky',
                  // A Chromium a sticky-határt a görgető doboz belső margóján BELÜL számolja: `bottom: 0`
                  // mellett 16 px rés maradt a sáv és a menü között (MÉRVE, 390/360 px) — ezért a
                  // Dashboard margójával (`MOBILE_SCROLLER_PADDING_BOTTOM`) lejjebb toljuk.
                  bottom: (t: Theme) => `calc(-1 * ${t.spacing(MOBILE_SCROLLER_PADDING_BOTTOM)})`,
                  // A lista VÉGÉN (ahol a sáv a természetes helyén áll) se maradjon üres sáv alatta:
                  // a negatív alsó margó „elnyeli" a gyökér és a görgető doboz alsó margóját.
                  mb: -(ATTENDANCE_ROOT_PADDING_XS + MOBILE_SCROLLER_PADDING_BOTTOM),
                  // SZÉLÉTŐL SZÉLIG [Web UX 9 / T3, 2026-10-13, validátor M1]: a gyökér ÉS a Dashboard görgető
                  // dobozának oldalsó margóját is ellensúlyozzuk (korábban 390 px-en x=8, width=374 maradt).
                  // Csak a margót tölti ki, nem lóg túl rajta → vízszintes görgetés nincs (E2E méri).
                  mx: -(ATTENDANCE_ROOT_PADDING_XS + MOBILE_SCROLLER_PADDING_X),
                  mt: 2,
                  px: 2,
                  py: 1,
                }
              : { position: 'fixed', left: 0, right: 0, bottom: 0, px: 3, py: 1.5 }),
            zIndex: (theme) => theme.zIndex.appBar - 1,
            display: 'flex',
            justifyContent: isSmallScreen ? 'space-between' : 'flex-end',
            alignItems: 'center',
            gap: isSmallScreen ? 1 : 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          {/*
            [Web 7 / T2, 2026-10-06] WEB_UX_2KOR 2.4: a `warning.main` fehér papíron 3,12:1 volt (AA: 4,5:1).
            Most a mentés-sáv TÉNYLEGES felszínén (sötétben a 8-as emelés fehér rétegével) mért,
            ≥ 4,5:1-es narancs (`unsavedCounterColor`), félkövér, és figyelmeztető ikon is jelzi —
            így nem csak a szín hordozza a jelentést.
          */}
          <Typography
            variant={isMobile ? 'body2' : 'body1'}
            data-testid="attendance-unsaved-count"
            sx={{
              fontWeight: unsavedRowCount > 0 ? 700 : 400,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              color: unsavedRowCount > 0 ? (t: Theme) => unsavedCounterColor(t) : 'text.secondary',
            }}
          >
            {unsavedRowCount > 0 && <WarningAmberIcon fontSize="small" aria-hidden data-testid="attendance-unsaved-icon" />}
            {unsavedRowCount > 0 ? `${unsavedRowCount} mentetlen változás` : 'Nincs mentetlen változás'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveAttendance}
            disabled={saving}
            /* A KÖZÖS testid [B-M8, 2026-09-16]: MINDKÉT elrendezésben EGY van, itt, a sávban
               [Web UX 9 / T3]. Érintéses (<900 px) nézetben 44 px-es célterület, mint a kártya gombjai. */
            data-testid="attendance-save"
            sx={isSmallScreen ? { minHeight: 44, flexShrink: 0 } : undefined}
          >
            {saving ? 'Mentés...' : 'Mentés'}
          </Button>
        </Paper>
      </>
    );
  };

  /**
   * A FÓKUSZÁLT MEZŐ NE KERÜLJÖN A MENTÉS-SÁV ALÁ [Web UX 9 / T3, 2026-10-13].
   * A böngésző fókuszkor csak akkor görget, ha a mező a nézeten KÍVÜL van — a rögzített sáv alatt
   * „látszik", tehát billentyűzettel (Tab) a következő mezőre lépve a sáv takarná. Ha a mező alja
   * a sáv teteje alá lóg, a nézet közepére görgetjük (ugyanúgy, mint a hibás összeg-mezőnél).
   * Csak a lap SAJÁT mezőire hat (a portálos dialógusok mezőire nem — a React-esemény átbuborékol a
   * portálon). ⚠️ A telefonos KÉPERNYŐ-billentyűzet (visual viewport) Playwright-ban nem mérhető —
   * azt kézzel kell ellenőrizni.
   *
   * ⚠️ EGÉRREL / ÉRINTÉSSEL FÓKUSZÁLT MEZŐNÉL A KÓD SOHA NEM GÖRGET — sem jelölőnél, sem szöveges
   * mezőnél; a görgetés CSAK billentyűzetes (Tab / Shift+Tab) fókusznál fut
   * [Web UX 9 / T3, 2026-10-13, validátor B1 + F-új1, koordinátori döntés].
   * - B1 (jelölő): a jelölő MOUSEDOWN-kor kap fókuszt; ha ekkor görgettünk, a mouseup már MÁS elemre
   *   esett, a `click` elmaradt — a pipa NEM került be (MÉRVE: 390 px tap, 390/1280 px egér).
   * - F-új1 (szöveges mező): az előző javítás a szöveges mezőt (összeg, megjegyzés) a kattintás UTÁN
   *   (`mouseup` + 0 ms) görgette — a volt `EventAttendance.tsx:~2561/~2569`. Ez elvitte a
   *   MÁSODIK kattintást/koppintást: 360 px-en dupla kattintásnál a második egy MÁSIK játékos
   *   összeg-mezőjébe esett és ott maradt a fókusz (a beírt összeg rossz sorba kerülhetett);
   *   390/1280 px-en a fókusz elveszett (mérő: scratchpad `val2/probe_dbl.js`).
   * Indok: a felhasználó a LÁTHATÓ részre kattintott, a mező használható; a telefonos
   * képernyő-billentyűzet megnyitásakor a böngésző maga görgeti a fókuszált mezőt. A billentyűzetes
   * fókusz azonnal görget — asztalon (≥900 px) is kell, mert ott a `fixed` sáv takarja a Tab-bal
   * elért mezőt. Őr: `flow-attendance-v2.spec.ts` „B1" és „F-új1" esetei (valódi `mouse.click` /
   * `mouse.dblclick` / `touchscreen.tap` / Tab) és a `sortFilterSaveBar` Jest.
   */
  const keepFocusedFieldAboveSaveBar = (e: React.FocusEvent<HTMLElement>) => {
    const viaPointer = pointerFocusPendingRef.current;
    pointerFocusPendingRef.current = false;
    const bar = saveBarRef.current;
    const target = e.target as HTMLElement;
    if (!bar || bar.contains(target) || !attendanceRootRef.current?.contains(target)) return;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    if (viaPointer) return;
    const barTop = bar.getBoundingClientRect().top;
    const r = target.getBoundingClientRect();
    if (r.height > 0 && r.bottom > barTop && r.top < window.innerHeight) {
      target.scrollIntoView?.({ block: 'center' });
    }
  };

  const renderDesktopTable = () => (
    /* [Web 7 / T2 javító kör 1, 2026-10-06 — F1] Az emelés a színrecepttel KÖZÖS konstans
       (`ATTENDANCE_TABLE_ELEVATION` = a MUI alapértéke, 1): sötét témában a `Paper` fehér rétege
       ezen a felszínen jelenik meg, és az `attendanceToneText` erre a felszínre is számol. */
    <Paper elevation={ATTENDANCE_TABLE_ELEVATION} sx={{ mb: 3 }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6">
            Jelenlét és Fizetés
          </Typography>
          {/* Esemény-szintű jelölés EGYSZER a fejlécben — ugyanaz, mint a telefonos kártya fejlécében [Web UX B/6] */}
          {selectedEvent?.isPaidOut && (
            <Chip
              label="Kifizetett – nem módosítható"
              color="success"
              size="small"
              sx={{ fontWeight: 'bold' }}
              data-testid="attendance-paid-out-chip"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CommentIcon />}
            onClick={() => setCommentDialogOpen(true)}
          >
            Megjegyzések ({comments.length})
          </Button>
          {canManageParticipants && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenAddParticipantDialog}
            >
              Résztvevő hozzáadás
            </Button>
          )}
          {canEditEvent() && (
            <>
              <Button
                variant="outlined"
                color="success"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllPresent}
                disabled={saving}
              >
                Összes jelen
              </Button>
              {/* A Mentés gomb INNEN a lenti, rögzített mentés-sávba KÖLTÖZÖTT [Web UX 3. csomag (B),
                  2026-10-04] — a testid vele ment, asztali elrendezésben EGY `attendance-save` van
                  (és [Web UX 9 / T3] a kártyás ágon is, ugyanabban a sávban — `renderSaveBar`). */}
            </>
          )}
        </Box>
      </Box>
      
      {/* `rosterPending` is [S-3]: a RÉGI névsor nem rajzolható ki az ÚJ esemény ÍVE alá. */}
      {loading || rosterPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
        {/* „Név keresése" (10 fő felett) [Web UX 3. csomag (B), 2026-10-04] */}
        {showNameFilter && (
          <Box sx={{ px: 2, pb: 1, maxWidth: 520 }}>
            {renderNameFilter()}
            {isNameFiltered && visibleAttendanceRecords.length === 0 && renderNoFilterMatch()}
          </Box>
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Név</TableCell>
                <TableCell align="center">Pótlási kredit</TableCell>
                <TableCell align="center">Jelen</TableCell>
                <TableCell align="center">Pótlás</TableCell>
                <TableCell align="center">Fizetett / Beszedett</TableCell>
                <TableCell align="center">Fizetett összeg (Ft)</TableCell>
                {attendanceRecords.some(r => r.recordedByName) && (
                  <>
                    <TableCell>Rögzítette</TableCell>
                    <TableCell>Rögzítés ideje</TableCell>
                  </>
                )}
                {canManageParticipants && (
                  <TableCell align="center">Műveletek</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Rendezett + szűrt NÉZET [Web UX 3. csomag (B), 2026-10-04] — az állapot sorrendje nem változik. */}
              {visibleAttendanceRecords.map((record) => {
                const userMakeupCredits = makeupCredits.get(record.userId) || 0;
                const canUseMakeup = record.isPresent && userMakeupCredits > 0;

                // KÖZÖS sor-gyökér testid a kártyás ággal [B-M8, 2026-09-16] — lásd `:1706`.
                return (
                <TableRow key={record.userId} data-testid="attendance-row">
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {onNavigateToUserProfile ? (
                        <Link
                          component="button"
                          variant="body1"
                          onClick={() => onNavigateToUserProfile(record.userId)}
                          sx={{
                            textAlign: 'left',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            // [Web 7 / T2, 2026-10-06] A név-link `primary.main` színe a böngészőben MÉRVE (E2E
                            // `flow-readable-highlights`): a jelenlévő kártyán világosban 3,91:1, sötétben 2,09:1
                            // (a sötét téma #00695C-je sötét háttéren). Az `attendanceToneText` minden háttéren ≥ 4,5:1.
                            color: (t: Theme) => attendanceToneText(t, 'primary'),
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {record.userName}
                        </Link>
                      ) : (
                        record.userName
                      )}
                      {record.unreadNotesCount != null && record.unreadNotesCount > 0 && (
                        <Chip
                          icon={<UnreadIcon sx={{ fontSize: '0.75rem' }} />}
                          label={record.unreadNotesCount}
                          color="error"
                          size="small"
                          sx={{ height: '20px', fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    {/* Edző sora: Végig / Részben — a kifizetés a tényleges órát számolja */}
                    {isCoachRow(record.userId) && record.isPresent && selectedEvent && (
                      <Box sx={{ mt: 1 }}>
                        <CoachPresenceControl
                          eventStart={selectedEvent.startTime}
                          eventEnd={selectedEvent.endTime}
                          value={presenceWindowOf(record)}
                          onChange={w => handlePresenceWindowChange(record.userId, w)}
                          disabled={!canEditEvent()}
                          testIdPrefix={`coach-presence-${record.userId}`}
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {/*
                      ISMERETLEN ≠ 0 [B1, 2026-09-15]. Itt korábban MINDEN hibára „0" jelvény
                      került, mert a lekérés `catch`-e 0-t írt a térképbe — az edző ebből azt
                      olvasta ki, hogy a játékosnak nincs kreditje, és készpénzt szedett be.
                      A „?" jelvény a buborékban meg is mondja, hogy a szám nem volt lekérdezhető.
                    */}
                    {isCreditUnknown(record.userId) ? (
                      <Chip
                        label="?"
                        size="small"
                        color="warning"
                        sx={{ minWidth: 40 }}
                        data-testid={`makeup-credit-unknown-${record.userId}`}
                        title="A pótlási kredit nem volt lekérdezhető — ez NEM azt jelenti, hogy nincs kreditje."
                      />
                    ) : userMakeupCredits > 0 ? (
                      <Chip
                        label={userMakeupCredits}
                        size="small"
                        color="info"
                        sx={{ minWidth: 40 }}
                      />
                    ) : (
                      <Chip
                        label="0"
                        size="small"
                        color="default"
                        sx={{ minWidth: 40 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={record.isPresent}
                      onChange={() => handleAttendanceChange(record.userId, 'isPresent')}
                      disabled={!canEditEvent()}
                      color="success"
                      icon={<CancelIcon />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={record.isMakeup}
                      onChange={() => handleAttendanceChange(record.userId, 'isMakeup')}
                      disabled={!canEditEvent() || !canUseMakeup}
                      color="info"
                      // ISMERETLEN kredit esetén a buborék KIMONDJA, hogy nem tudjuk — nem azt,
                      // hogy nincs [B1]. A jelölő mindkét esetben zárt (biztonságos irány).
                      title={makeupTitle(record.userId, record.isPresent)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Checkbox
                        checked={record.hasPaidOnSite}
                        onChange={() => handleAttendanceChange(record.userId, 'hasPaidOnSite')}
                        disabled={!canEditEvent()}
                        color="success"
                      />
                      {/* [Web 7 / T2 javító kör 1, 2026-10-06 — F1] Az asztali tábla is a közös receptet használja:
                        a `warning.dark` „Fizetett" világosban 3,79:1, a `success.dark` „Beszedett" sötétben (a `Paper`
                        rétegével) 2,81:1 volt — és eltért a „Hogyan olvasd" jelmagyarázattól. */}
                    <Typography variant="caption" data-testid="attendance-paid-caption" sx={{ color: (t: Theme) => attendanceToneText(t, isCoachRow(record.userId) ? 'success' : 'warning'), fontWeight: 600, lineHeight: 1 }}>
                        {isCoachRow(record.userId) ? 'Beszedett' : 'Fizetett'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      {record.hasPaidOnSite ? (
                        <TextField
                          key={record.userId}
                          size="small"
                          type="text"
                          inputProps={{
                            'data-testid': `attendance-amount-${record.userId}`,
                            inputMode: 'numeric',
                          }}
                          // A mentés-őr IDE fókuszál és IDE görget [M2, 2026-09-15].
                          inputRef={registerPaymentInput(record.userId)}
                          error={paymentFieldError?.userId === record.userId}
                          helperText={paymentFieldError?.userId === record.userId ? paymentFieldError.message : undefined}
                          value={paymentFieldValue(record)}
                          onChange={(e) => handlePaymentAmountChange(record.userId, e.target.value, paymentFieldValue(record))}
                          disabled={!canEditEvent()}
                          sx={{ width: '100px' }}
                        />
                      ) : (
                        !record.privateLessonFee && '-'
                      )}
                      {record.privateLessonFee != null && (
                        /* [Web 7 / T2 javító kör 1, 2026-10-06 — F1] `primary.main` sötét témában 2,18:1 volt. */
                        <Typography variant="caption" data-testid="attendance-fee-due" sx={{ color: (t: Theme) => attendanceToneText(t, 'primary'), fontWeight: 700, whiteSpace: 'nowrap' }}>
                          Beszedendő: {record.privateLessonFee.toLocaleString('hu-HU')} Ft
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {attendanceRecords.some(r => r.recordedByName) && (
                    <>
                      <TableCell>{record.recordedByName || '-'}</TableCell>
                      <TableCell>
                        {/* [Web UX 1. csomag (4.3), 2026-10-03] UTC, „Z” nélkül → közös segéd (budapesti idő). */}
                        {formatServerDateTime(record.recordedDate, { fallback: '-' })}
                      </TableCell>
                    </>
                  )}
                  {canManageParticipants && (
                    <TableCell align="center">
                      {addedParticipantIds.has(record.userId) && (
                        <Tooltip title="Résztvevő eltávolítása">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveParticipant(record.userId)}
                            aria-label={`Résztvevő eltávolítása: ${record.userName}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {/* Helyszíni beszedés – tételes kimutatás edzőnként (RecordedBy alapján) + magyarázó.
          Ez alapján történik az edző-kifizetés levonása. */}
      {hasAnyCollection && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', mb: 1.5 }}>
              Beszedések edzőnként → ennyi a levonás
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {coachCollections.map((c, i) => (
                /* [Web 7 / T2, 2026-10-06] WEB_UX_2KOR 4.1: a `success.lighter` nem létező kulcs volt (nem látszó
                   háttér); a `success.dark` összeg sötét témában 3,25:1. Közös recept: `collectionRowColors`. */
                <Box key={`coach-${i}`} data-testid="attendance-coach-collection-row" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 1.5, py: 1, borderRadius: 1, bgcolor: (t: Theme) => collectionRowColors(t).bg }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: (t: Theme) => collectionRowColors(t).amount, whiteSpace: 'nowrap' }}>−{formatHuf(c.amount)}</Typography>
                </Box>
              ))}
              {nonCoachCollections.map((c, i) => (
                <Box key={`other-${i}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 1.5, py: 1, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{c.name} · admin szedte be — <Box component="span" sx={{ fontWeight: 700 }}>nem levonás</Box></Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{formatHuf(c.amount)}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', mb: 1.5 }}>
              Hogyan olvasd
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {/* [Web 7 / T2] ugyanaz a szín, mint a kártya címkéin (`attendanceToneText`) — sötétben a `success.dark` 3,25:1 volt. */}
              <Box component="span" data-testid="attendance-legend-collected" sx={{ color: (t: Theme) => attendanceToneText(t, 'success'), fontWeight: 700 }}>Beszedett</Box> = az edző szedte be a pénzt a helyszínen. <Box component="span" data-testid="attendance-legend-paid" sx={{ color: (t: Theme) => attendanceToneText(t, 'warning'), fontWeight: 700 }}>Fizetett</Box> = a játékos a saját díját fizette készpénzben.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A levonás a <Box component="span" sx={{ fontWeight: 700 }}>Rögzítette</Box> szerint történik: minden edző kifizetéséből csak azt vonjuk le, amit ő rögzített. Amit admin szedett be, az egyik edzőt sem terheli.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* A rögzített mentés-sáv — KÖZÖS a két elrendezésben (`renderSaveBar`) [Web UX 9 / T3]. */}
      {renderSaveBar()}
    </Paper>
  );

  return (
    <Box
      ref={attendanceRootRef}
      sx={{ p: { xs: ATTENDANCE_ROOT_PADDING_XS, md: 3 } }}
      // [Web UX 9 / T3, 2026-10-13, validátor B1] a mutatós fókusz jelölése (lásd `keepFocusedFieldAboveSaveBar`)
      onPointerDownCapture={markPointerFocus}
      onMouseDownCapture={markPointerFocus}
      onTouchStartCapture={markPointerFocus} onFocus={keepFocusedFieldAboveSaveBar}>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
        Jelenlét nyilvántartás
      </Typography>

      {/* Event Selection - Enhanced for mobile */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
            <Box sx={{ width: { xs: '100%', md: '25%' } }}>
              <TextField
                label="Dátum"
                type="date"
                value={selectedDate ? formatDateToLocal(selectedDate) : ''}
                onChange={async (e) => {
                  // A dátumváltás új napot (és új esemény-listát) tölt, tehát eldobja a mentetlen
                  // jelöléseket — előbb kérdezünk. Az értéket az `await` ELŐTT olvassuk ki.
                  const value = e.target.value;
                  if (!(await confirmDiscardUnsaved())) return;
                  // Kézi dátumváltásnál a cél-esemény elfelejtendő: a felhasználó már más
                  // napot néz, oda nem szabad áthúzni egy másik nap eseményét.
                  pendingEventIdRef.current = undefined;
                  setSelectedDate(value ? new Date(value) : null);
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputBase-root': {
                    minHeight: isMobile ? 44 : 'auto'
                  }
                }}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', md: '75%' } }}>
              <FormControl fullWidth>
                <InputLabel>Válasszon eseményt</InputLabel>
                <Select
                  value={selectedEvent?.eventId || ''}
                  onChange={async (e) => {
                    const eventId = e.target.value;
                    // Ugyanarra az eseményre „váltani" nem kilépés — ilyenkor ne kérdezzünk.
                    if (eventId === (selectedEvent?.eventId || '')) return;
                    // Az eseményváltás a helyi sorokat ÉS az alapvonalat is nullázza (a régi esemény
                    // mentetlen jelölései nem vihetők át), ezért előbb megerősítést kérünk.
                    if (!(await confirmDiscardUnsaved())) return;
                    const event = events.find(ev => ev.eventId === eventId);
                    setSelectedEvent(event || null);
                  }}
                  label="Válasszon eseményt"
                  sx={{
                    minHeight: isMobile ? 44 : 'auto'
                  }}
                >
                  {events.map(event => (
                    <MenuItem key={event.eventId} value={event.eventId}>
                      {format(new Date(event.eventDate), 'yyyy.MM.dd', { locale: hu })} - {event.eventName}
                      {event.venueName && ` (${event.venueName})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          {selectedEvent && !isMobile && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {selectedEvent.venueName && (
                <Chip label={selectedEvent.venueName} />
              )}
              {selectedEvent.eventTypeName && (
                <Chip label={selectedEvent.eventTypeDisplayName || selectedEvent.eventTypeName} color="secondary" />
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Statistics Cards - Compact for mobile */}
      {selectedEvent && !isMobile && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon color="primary" fontSize={isMobile ? "small" : "medium"} />
                  <Typography color="text.secondary" variant={isMobile ? "body2" : "body1"}>
                    Résztvevők
                  </Typography>
                </Box>
                <Typography variant={isMobile ? "h5" : "h4"}>
                  {presentCount} / {totalParticipants}
                  {selectedEvent?.maxParticipants && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Kapacitás: {totalParticipants}/{selectedEvent.maxParticipants}
                    </Typography>
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" fontSize={isMobile ? "small" : "medium"} />
                  <Typography color="text.secondary" variant={isMobile ? "body2" : "body1"}>
                    Jelenlét
                  </Typography>
                </Box>
                <Typography variant={isMobile ? "h5" : "h4"}>
                  {attendancePercentage}%
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon color="success" fontSize={isMobile ? "small" : "medium"} />
                  <Typography color="text.secondary" variant={isMobile ? "body2" : "body1"}>
                    Fizetett
                  </Typography>
                </Box>
                <Typography variant={isMobile ? "h5" : "h4"}>
                  {paidCount} fő
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Hiba MARADÓ sáv (a felhasználónak látnia kell, akár hosszan is); a siker a globális
          toastra ment át (FeedbackContext) — lásd a `showSuccess` hívásokat lejjebb. A táblázat/kártya
          TETEJE fölött ide tett siker-Alertet 15+ fős névsornál az edző legörgetve nem látta. */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/**
        * BETÖLTÉSI hiba — LÁTHATÓ, és ÚJRAPRÓBÁLHATÓ [BL-4, 2026-09-15].
        *
        * MI VOLT A BAJ: a résztvevő-névsor lekérésének hibája CSAK a konzolra ment
        * (`console.error` + `setUsers([])`), a jogosultság pedig ebből az üres névsorból dőlt el —
        * egy átmeneti hálózati hiba tehát az EGÉSZ ívet zárolta (pipák, összeg-mezők, Mentés),
        * HIBAÜZENET NÉLKÜL. A hozzárendelt edző nem tudta, miért nem tud dolgozni.
        *
        * Az `'participantsUnknown'` kiegészítő mondat azt mondja ki, hogy a zárolás a betöltés
        * bukása miatt van, NEM jogosultság miatt — és hogy az „Újra" feloldhatja.
        */}
      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-testid="attendance-load-error"
          action={
            <Button color="inherit" size="small" onClick={loadError.retry} data-testid="attendance-load-retry">
              Újra
            </Button>
          }
        >
          {loadError.message}
          {loadError.kind === 'participants' && lockReason() === 'participantsUnknown' && (
            ' A szerkesztési jogosultság emiatt nem megállapítható, ezért a jelölők és a Mentés zárva vannak. Töltsd be újra a névsort.'
          )}
        </Alert>
      )}

      {/**
        * A PÓTLÁSI KREDIT LEKÉRÉSÉNEK HIBÁJA — LÁTHATÓ, és ÚJRAPRÓBÁLHATÓ [B1, 2026-09-15].
        *
        * MI VOLT A BAJ: a lekérés `catch`-e MINDEN hibára 0 kreditet írt be (csak a naplózás
        * szűrt 404-re). Egy átmeneti 500 tehát „nincs kreditje"-ként jelent meg: a „Pótlás"
        * jelölő letiltva, a „N pótlás" címke sehol — és az edző KÉSZPÉNZT szedett be a kredites
        * játékostól. Most az ismeretlen állapot „?"-ként látszik, a jelölő zárva marad (vaktában
        * nem adunk kreditet), és ez a sáv kimondja, mi történt.
        *
        * MIÉRT SÁV ÉS NEM CSAK TOAST: pénzről dönt, tehát nem tűnhet el pár másodperc után.
        * A toast (`showWarning`) EMELLETT megy el, mert hosszú névsornál a lap teteje nem látszik.
        */}
      {creditsError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="attendance-credits-error"
          action={
            <Button color="inherit" size="small" onClick={creditsError.retry} data-testid="attendance-credits-retry">
              Újra
            </Button>
          }
        >
          {creditsError.message}
        </Alert>
      )}

      {/* Kártyás elrendezés 900 px alatt (telefon + tablet), táblázat felette */}
      {selectedEvent && (
        <>
          {isSmallScreen ? (
            renderMobileEventCard(selectedEvent)
          ) : (
            renderDesktopTable()
          )}
          {/* Asztalon a sáv a táblázat `Paper`-jében van (fixed); a kártyás ágon itt, a lap gyökerében
              — telefonon `sticky`, ehhez a gyökér görgető-dobozában kell lennie [Web UX 9 / T3]. */}
          {isSmallScreen && renderSaveBar()}
        </>
      )}

      {/* Add Participant Dialog */}
      <Dialog
        open={addParticipantDialogOpen}
        onClose={handleCloseAddParticipantDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Résztvevő hozzáadása</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {/* A felhasználó-lista lekérésének hibája LÁTSZIK, „Újra" gombbal [BL-4]: eddig a
                dialógus csak „Nincs elérhető felhasználó"-t mutatott egy elbukott lekérés után. */}
            {addParticipantError && (
              <Alert
                severity="error"
                data-testid="add-participant-error"
                action={
                  <Button color="inherit" size="small" onClick={fetchAvailableUsersAndRoles} data-testid="add-participant-retry">
                    Újra
                  </Button>
                }
              >
                {addParticipantError}
              </Alert>
            )}
            {/* [7. javító kör — validátor F3] a HOZZÁADÁS hibája (a fenti a lista-betöltésé, „Újra" gombbal) */}
            {addSaveError && (
              <Alert severity="error" data-testid="add-participant-save-error">
                {addSaveError}
              </Alert>
            )}
            {/* [C10, 2026-10-30] MI VOLT A BAJ: a címke `${user.name} (${user.email})` volt — a szerver
                edzőnek MÁS tag e-mailjét `null`-ként adja (`backend/PinyoApi/Services/UserListSanitizer.cs`),
                így „Név (null)" lett volna. `userOptionLabel`: e-mail nélkül csak a név (admin: változatlan).
                A sor-kulcs és az azonosság a `userId`-n áll, NEM a címkén/e-mailen (azonos nevű tagok). */}
            <Autocomplete
              options={availableUsers}
              getOptionLabel={(user) => userOptionLabel(user)}
              isOptionEqualToValue={(a, b) => a.userId === b.userId}
              renderOption={(props, user) => {
                const { key, ...optionProps } = props as any;
                return (
                  <li key={user.userId} {...optionProps}>
                    {userOptionLabel(user)}
                  </li>
                );
              }}
              value={selectedUserToAdd}
              onChange={(_, newValue) => setSelectedUserToAdd(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Felhasználó"
                  required
                />
              )}
              noOptionsText="Nincs elérhető felhasználó"
            />

            <FormControl fullWidth required>
              <InputLabel>Szerepkör</InputLabel>
              <Select
                value={selectedRoleForNewParticipant}
                label="Szerepkör"
                onChange={(e) => setSelectedRoleForNewParticipant(e.target.value)}
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.userRoleId} value={role.userRoleId}>
                    {role.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddParticipantDialog}>
            Mégse
          </Button>
          <Button
            onClick={handleAddParticipant}
            variant="contained"
            disabled={!selectedUserToAdd || !selectedRoleForNewParticipant || addingParticipant}
          >
            {addingParticipant ? 'Hozzáadás...' : 'Hozzáadás'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comments Dialog - Enhanced for mobile */}
      <Dialog
        open={commentDialogOpen}
        onClose={closeCommentDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: isMobile ? 0 : 2,
            maxHeight: isMobile ? '100vh' : 'calc(100% - 64px)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Esemény megjegyzések
          {selectedEvent && isMobile && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedEvent.eventName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {/* [7. javító kör — validátor F3] a megjegyzés mentésének hibája a dialógusban (a lap-sáv mögötte van) */}
          {commentError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="comment-dialog-error">
              {commentError}
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            {comments.length === 0 ? (
              <Typography color="text.secondary">
                Még nincs megjegyzés ehhez az eseményhez.
              </Typography>
            ) : (
              comments.map((comment) => ( // Fixed property names
                <Paper key={comment.commentId} sx={{ p: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {/* [Web UX 1. csomag (4.3), 2026-10-03] a megjegyzés `createdDate`-je UTC, „Z” nélkül (`EventCommentsController.cs:104`). */}
                    {comment.coachName} - {formatServerDateTime(comment.createdDate, { withoutYear: isMobile })}
                  </Typography>
                  <Typography sx={{ mt: 0.5 }}>{comment.comment}</Typography>
                </Paper>
              ))
            )}
          </Box>
          
          <TextField
            fullWidth
            multiline
            rows={isMobile ? 4 : 3}
            label="Új megjegyzés"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiInputBase-root': {
                minHeight: isMobile ? 120 : 'auto'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={closeCommentDialog}
            data-testid="comment-dialog-cancel"
            sx={{ minHeight: isMobile ? 44 : 'auto' }}
          >
            Mégse
          </Button>
          <Button 
            onClick={handleAddComment} 
            variant="contained" 
            disabled={!newComment.trim()}
            sx={{ 
              minHeight: isMobile ? 44 : 'auto',
              flex: isMobile ? 1 : 'none'
            }}
          >
            Megjegyzés hozzáadása
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventAttendance;
